import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, updateDoc, deleteDoc, addDoc, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db, functions } from '@/lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import Header from '@/components/layout/Header';
import { DashboardContentContainer } from '@/components/layout/DashboardContentContainer';
import { Event, RSVP, RSVPStatus, Invoice, UnitType, Table } from '@/types';
import { formatDate, formatCurrency, cn } from '@/lib/utils';
import { ArrowLeft, Edit, Trash2, X, Users, CheckCircle, Download, FileText, FileSpreadsheet, Ban, ChevronDown, User, UserPlus, Link, Clock, Wallet, Contact, Loader2, Minus, Plus } from 'lucide-react';
import { showSuccess, showError, showWarning, confirmAction } from '@/lib/swal';
import {
  describePlaceCardGenerationProblems,
  exportToCSV,
  generateAttendanceList,
  generateCustomPlaceCards,
} from '@/lib/pdf-generator';
import { loadEventTablePlanTables } from '@/lib/table-plan-seating';
import { loadEventPlaceCardTemplate } from '@/lib/event-place-card-template';
import { useUnit } from '@/contexts/UnitContext';
import { mapEventData, mapRsvpData, getRsvpExpectedAmount, isActiveInvoiceData, computeAttendeeDeltaForApologyToggle, isAttendeeCountingRsvp, computeRsvpSubtotalForInvoice } from '@/lib/event-utils';
import { normalizeTicketCoursesToFormatA, getSelectionFromFormatA } from '@/lib/ticket-courses';
import { useRanks } from '@/hooks/useRanks';
import { EventHeaderCredits } from '@/components/event/EventHeaderCredits';
import { useAuth } from '@/contexts/AuthContext';
import { DataTable } from '@/components/ui/data-table';
import { MultiSelect, MultiSelectOption } from '@/components/ui/multi-select';
import { ColumnDef, VisibilityState } from '@tanstack/react-table';
import {
  getAttendanceExportAmounts,
  getAttendanceRsvpPaymentState,
  getInvoicePayerRsvp,
} from '@/lib/attendance-payment-state';
import {
  ensureInvoiceForBooking,
  mapFirestoreInvoiceData,
  recalcHostInvoicesAfterGuestPaymentEdit,
} from '@/lib/ensure-invoice-for-rsvp';
import {
  buildGuestRsvpCreateEmailFlags,
  buildGuestRsvpEditEmailFlags,
} from '@/lib/attendance-guest-email';
import { getCourseSelectOptions, isMealCourseSelectionComplete } from '@/lib/meal-course-options';
import { formatRsvpQuestionAnswers, getRsvpQuestionAnswers } from '@/lib/rsvp-question-answers';
import { InvoicePaymentsModal } from '@/components/event/InvoicePaymentsModal';
import { isMultiTenantEvent } from '@/lib/ticket-mode';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/** Sentinel value for RSVPs with no prefix (empty rank) in the prefix filter */
const PREFIX_FILTER_NONE = '__prefix_none__';

/** Ticket display names on RSVP for global table search (matches meal column name resolution). */
function getRsvpTicketNamesForSearch(rsvp: RSVP, event: Event): string {
  if (!event.tickets?.length) return '';
  const lines = rsvp.responses?.Tickets;
  if (!Array.isArray(lines)) return '';
  const parts: string[] = [];
  for (const line of lines as Array<{ id?: string; name?: string; qty?: number }>) {
    if (!line || Number(line.qty || 0) <= 0) continue;
    const fromLine = typeof line.name === 'string' ? line.name.trim() : '';
    const id = typeof line.id === 'string' ? line.id.trim() : '';
    const fromEvent = id
      ? event.tickets?.find((tt) => tt.id === id)?.name?.trim() ?? ''
      : '';
    const label = fromLine || fromEvent;
    if (label) parts.push(label);
  }
  return parts.join(' ');
}

/** Sort hosts for dropdowns: first name, then last name (uses `firstName`/`lastName`, else first token of `name` vs rest). */
function compareRsvpFirstNameLastName(a: RSVP, b: RSVP): number {
  const key = (r: RSVP) => {
    const fn = (r.firstName ?? '').trim();
    const ln = (r.lastName ?? '').trim();
    if (fn || ln) {
      return { first: fn, last: ln };
    }
    const raw = (r.name ?? '').trim();
    if (!raw) {
      return { first: '', last: '' };
    }
    const parts = raw.split(/\s+/).filter(Boolean);
    if (parts.length <= 1) {
      return { first: raw, last: '' };
    }
    return { first: parts[0], last: parts.slice(1).join(' ') };
  };
  const ka = key(a);
  const kb = key(b);
  const c = ka.first.localeCompare(kb.first, undefined, { sensitivity: 'base' });
  if (c !== 0) return c;
  return ka.last.localeCompare(kb.last, undefined, { sensitivity: 'base' });
}

export default function AttendanceListPage() {
  const { unitId, eventId } = useParams<{ unitId: string; eventId: string }>();
  const navigate = useNavigate();
  const { ranks } = useRanks();
  const { userProfile } = useAuth();
  const [event, setEvent] = useState<Event | null>(null);
  const [rsvps, setRsvps] = useState<RSVP[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRsvp, setSelectedRsvp] = useState<RSVP | null>(null);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [invoiceMap, setInvoiceMap] = useState<Record<string, Invoice>>({});
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentModalInvoice, setPaymentModalInvoice] = useState<Invoice | null>(null);
  const [guestRelationships, setGuestRelationships] = useState<{
    invitedBy?: RSVP;
    guests: RSVP[];
  }>({ guests: [] });
  const [editingRsvp, setEditingRsvp] = useState<RSVP | null>(null);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    rank: '',
    additionalRank: '',
    unit: '',
    specialRequests: '',
    responses: {} as Record<string, any>,
    apologies: false,
    status: 'confirmed' as RSVPStatus,
    invitedBy: '',
    inviterPaysFor: true,
    notifyGuestByEmail: false,
  });
  const [expandedHosts, setExpandedHosts] = useState<Record<string, boolean>>({});
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [tableAssignments, setTableAssignments] = useState<Record<string, { tableName: string; seatNumber: number }>>({});
  const [placeCardTables, setPlaceCardTables] = useState<Table[]>([]);
  const [generatingPlaceCardRsvpId, setGeneratingPlaceCardRsvpId] = useState<string | null>(null);
  const [approvingPaymentRsvpId, setApprovingPaymentRsvpId] = useState<string | null>(null);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  const { units, loading: unitsLoading } = useUnit();
  const [showApologyModal, setShowApologyModal] = useState(false);
  const [creatingApology, setCreatingApology] = useState(false);
  const [apologyForm, setApologyForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    rank: '',
    additionalRank: '',
    unit: '',
    specialRequests: '',
  });
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [creatingGuest, setCreatingGuest] = useState(false);
  const [guestForm, setGuestForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    rank: '',
    additionalRank: '',
    unit: '',
    specialRequests: '',
    invitedBy: '',
    selectedTicketId: '',
    ticketCourseSelections: {} as Record<string, Record<number, Record<string, string>>>,
    courseSelections: {} as Record<string, string>,
    notifyGuestByEmail: false,
  });
  const [exportFilter, setExportFilter] = useState<'all' | 'attending' | 'apologies'>('all');
  const [unitTypePrefixes, setUnitTypePrefixes] = useState<Array<{ id: string; name: string; shortName: string }>>([]);
  const [prefixFilterSelection, setPrefixFilterSelection] = useState<string[]>([]);

  const isAdmin = userProfile?.isAdmin === true;
  const userRole = userProfile?.units?.find((u) => u.unitId === event?.unitId)?.role;
  const canApprovePayments = isAdmin || userRole === 'Secretary' || userRole === 'Treasurer';
  const NO_HOST_VALUE = '__no_host__';
  const hasUnitTypePrefixes = unitTypePrefixes.length > 0;

  const getRankShortName = (rankName: string): string | null => {
    const trimmedRankName = rankName.trim();
    if (!trimmedRankName) {
      return null;
    }

    const matchedUnitPrefix = unitTypePrefixes.find((prefix) => prefix.name === trimmedRankName);
    if (matchedUnitPrefix?.shortName) {
      return matchedUnitPrefix.shortName;
    }

    const matchedRank = ranks.find((rankItem) => rankItem.name === trimmedRankName);
    return matchedRank?.shortName || null;
  };

  const adminLog = (...args: any[]) => {
    if (isAdmin) {
      console.log('[ATTENDANCE ADMIN]', ...args);
    }
  };

  const adminGroupLog = (label: string, data: any[]) => {
    if (!isAdmin) {
      return;
    }

    console.group(`[ATTENDANCE ADMIN] ${label}`);
    data.forEach((item) => {
      console.log(item);
    });
    console.groupEnd();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportMenu]);

  const buildTableAssignments = (tables: any[]): Record<string, { tableName: string; seatNumber: number }> => {
    const assignments: Record<string, { tableName: string; seatNumber: number }> = {};

    const deriveTableName = (table: any, index: number): string => {
      const possibleNames = [
        typeof table.name === 'string' ? table.name.trim() : '',
        typeof table.tableName === 'string' ? table.tableName.trim() : '',
        typeof table.id === 'string' ? table.id.trim() : '',
      ].filter(Boolean);

      return possibleNames[0] || `Table ${index + 1}`;
    };

    tables.forEach((table: any, tableIndex: number) => {
      const tableName = deriveTableName(table, tableIndex);
      const seatingArray = Array.isArray(table.seating) ? table.seating : [];

      seatingArray.forEach((seat: any, seatIndex: number) => {
        const seatRsvpId = typeof seat.rsvpId === 'string' ? seat.rsvpId.trim() : '';
        if (!seatRsvpId) {
          return;
        }

        const seatNumber =
          typeof seat.seatNumber === 'number'
            ? seat.seatNumber
            : Number(seat.seatNumber || seatIndex + 1);

        assignments[seatRsvpId] = {
          tableName,
          seatNumber,
        };
      });
    });

    return assignments;
  };

  const loadTableAssignments = async (unitId: string) => {
    if (!eventId) {
      return;
    }

    try {
      // Table planner floor plans are the source of truth for table/seat numbers.
      const tables = await loadEventTablePlanTables(unitId, eventId);

      const assignments = buildTableAssignments(tables || []);
      setTableAssignments(assignments);
      setPlaceCardTables((tables || []) as Table[]);
    } catch (error) {
      console.error('Error fetching table plan:', error);
      // Swallow to avoid breaking attendance list load
    }
  };

  useEffect(() => {
    if (!eventId?.trim()) {
      setLoading(false);
      showError('Invalid event');
      return;
    }

    // Wait until unit memberships are resolved; otherwise we may finish with "not found" while units are still loading.
    if (unitsLoading) {
      return;
    }

    const fetchEventData = async () => {
      try {
        // Search only within accessible units
        let found = false;
        for (const u of units) {
          const eventDoc = await getDoc(doc(db, 'units', u.id, 'events', eventId));

          if (eventDoc.exists()) {
            setEvent(mapEventData(eventDoc, u.id));
            try {
              const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
              const unitTypes = settingsDoc.exists()
                ? (settingsDoc.data().unitTypes || []) as UnitType[]
                : [];
              const currentUnitType = unitTypes.find((unitType) => unitType.id === u.typeId);
              const prefixes = (currentUnitType?.ranks || [])
                .filter((prefix) => prefix.name && prefix.name.trim() !== '')
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                .map((prefix) => ({
                  id: prefix.id,
                  name: prefix.name.trim(),
                  shortName: prefix.shortName?.trim() || '',
                }));
              setUnitTypePrefixes(prefixes);
            } catch (unitTypeError) {
              console.error('Error loading unit type prefixes:', unitTypeError);
              setUnitTypePrefixes([]);
            }

            // Fetch RSVPs for this event
            const rsvpsSnapshot = await getDocs(collection(db, 'units', u.id, 'events', eventId, 'rsvps'));
            const mappedRsvps = rsvpsSnapshot.docs
              .map((docSnap) => mapRsvpData(docSnap as any))
              .filter((r): r is RSVP => r !== null)
              .map((r) => {
                r.eventId = eventId;
                return r;
              });

            adminLog('Fetched RSVPs count:', mappedRsvps.length);
            adminGroupLog(
              'RSVP Records',
              mappedRsvps.map((rsvpRecord) => ({
                id: rsvpRecord.id,
                name: rsvpRecord.name,
                email: rsvpRecord.email,
                userId: rsvpRecord.userId,
                invitedBy: rsvpRecord.invitedBy,
                status: rsvpRecord.status,
              })),
            );

            if (isAdmin) {
              const relationshipDiagnostics = mappedRsvps.map((rsvpRecord) => {
                const inviter = rsvpRecord.invitedBy
                  ? mappedRsvps.find(
                      (candidate) =>
                        candidate.userId === rsvpRecord.invitedBy || candidate.id === rsvpRecord.invitedBy,
                    )
                  : undefined;
                const guestsForRsvp = mappedRsvps.filter(
                  (candidate) => candidate.invitedBy === rsvpRecord.userId || candidate.invitedBy === rsvpRecord.id,
                );

                return {
                  rsvpId: rsvpRecord.id,
                  name: rsvpRecord.name,
                  inviterId: rsvpRecord.invitedBy || null,
                  matchedInviterId: inviter ? inviter.id : null,
                  matchedInviterUserId: inviter?.userId ?? null,
                  guestIds: guestsForRsvp.map((guest) => guest.id),
                  guestNames: guestsForRsvp.map((guest) => guest.name),
                };
              });

              adminGroupLog('RSVP Relationship Diagnostics', relationshipDiagnostics);
            }

            setRsvps(mappedRsvps);

            await loadTableAssignments(u.id);

            found = true;
            break;
          }
        }

        // If event not found in user's units and user is admin, search all units
        if (!found && isAdmin) {
          adminLog('Event not found in user units, searching all units (admin access)...');
          const unitsSnapshot = await getDocs(collection(db, 'units'));
          
          for (const unitDoc of unitsSnapshot.docs) {
            const unitId = unitDoc.id;
            adminLog('Checking unit:', unitId, unitDoc.data().name);
            const eventDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId));
            
            if (eventDoc.exists()) {
              adminLog('Event found in unit (admin access):', unitId);
              setEvent(mapEventData(eventDoc, unitId));

              // Fetch RSVPs for this event
              const rsvpsSnapshot = await getDocs(collection(db, 'units', unitId, 'events', eventId, 'rsvps'));
              const mappedRsvps = rsvpsSnapshot.docs
                .map((docSnap) => mapRsvpData(docSnap as any))
                .filter((r): r is RSVP => r !== null)
                .map((r) => {
                  r.eventId = eventId;
                  return r;
                });

              adminLog('Fetched RSVPs count:', mappedRsvps.length);
              adminGroupLog(
                'RSVP Records',
                mappedRsvps.map((rsvpRecord) => ({
                  id: rsvpRecord.id,
                  name: rsvpRecord.name,
                  email: rsvpRecord.email,
                  userId: rsvpRecord.userId,
                  invitedBy: rsvpRecord.invitedBy,
                  status: rsvpRecord.status,
                })),
              );

              if (isAdmin) {
                const relationshipDiagnostics = mappedRsvps.map((rsvpRecord) => {
                  const inviter = rsvpRecord.invitedBy
                    ? mappedRsvps.find(
                        (candidate) =>
                          candidate.userId === rsvpRecord.invitedBy || candidate.id === rsvpRecord.invitedBy,
                      )
                    : undefined;
                  const guestsForRsvp = mappedRsvps.filter(
                    (candidate) => candidate.invitedBy === rsvpRecord.userId || candidate.invitedBy === rsvpRecord.id,
                  );

                  return {
                    rsvpId: rsvpRecord.id,
                    name: rsvpRecord.name,
                    inviterId: rsvpRecord.invitedBy || null,
                    matchedInviterId: inviter ? inviter.id : null,
                    matchedInviterUserId: inviter?.userId ?? null,
                    guestIds: guestsForRsvp.map((guest) => guest.id),
                    guestNames: guestsForRsvp.map((guest) => guest.name),
                  };
                });

                adminGroupLog('RSVP Relationship Diagnostics', relationshipDiagnostics);
              }

              setRsvps(mappedRsvps);

              await loadTableAssignments(unitId);

              found = true;
              break;
            }
          }
        }

        if (!found) {
          adminLog('Event not found in any accessible unit');
          showError('Event not found');
        }
      } catch (error) {
        console.error('Error fetching event:', error);
        showError('Failed to load event');
      } finally {
        setLoading(false);
      }
    };

    void fetchEventData();
  }, [eventId, units, isAdmin, unitsLoading]);

  useEffect(() => {
    if (!event?.unitId || !event?.id) return;
    const uid = event.unitId;
    const eid = event.id;
    const eventRef = doc(db, 'units', uid, 'events', eid);
    const unsubEvent = onSnapshot(eventRef, (snap) => {
      if (snap.exists()) {
        setEvent(mapEventData(snap, uid));
      }
    });
    const rsvpsRef = collection(db, 'units', uid, 'events', eid, 'rsvps');
    const unsubRsvps = onSnapshot(query(rsvpsRef, orderBy('createdAt', 'desc')), (snapshot) => {
      const mappedRsvps = snapshot.docs
        .map((docSnap) => mapRsvpData(docSnap as any))
        .filter((r): r is RSVP => r !== null)
        .map((r) => {
          r.eventId = eid;
          return r;
        });
      setRsvps(mappedRsvps);
    });
    return () => {
      unsubEvent();
      unsubRsvps();
    };
  }, [event?.unitId, event?.id]);

  // Load invoices for all RSVPs that have an invoiceId (for payment status including pending_verification)
  useEffect(() => {
    if (!event?.unitId || !event?.id) {
      setInvoiceMap({});
      return;
    }
    const uid = event.unitId;
    const eid = event.id;
    const invoicesCol = collection(db, 'units', uid, 'events', eid, 'invoices');
    const unsub = onSnapshot(invoicesCol, (snapshot) => {
      const map: Record<string, Invoice> = {};
      snapshot.docs.forEach((snap) => {
        const d = snap.data();
        if (!isActiveInvoiceData(d)) {
          return;
        }
        map[snap.id] = mapFirestoreInvoiceData(snap.id, d as Record<string, unknown>);
      });
      setInvoiceMap(map);
    });
    return () => unsub();
  }, [event?.unitId, event?.id]);

  const handleViewRsvp = async (rsvp: RSVP) => {
    setSelectedRsvp(rsvp);

    adminLog('Viewing RSVP', {
      id: rsvp.id,
      name: rsvp.name,
      userId: rsvp.userId,
      invitedBy: rsvp.invitedBy,
    });

    // Find guest relationships
    // Try matching by userId first, then by RSVP ID (for backward compatibility)
    const invitedByRsvp = rsvp.invitedBy 
      ? rsvps.find(r => r.userId === rsvp.invitedBy || r.id === rsvp.invitedBy)
      : undefined;
    
    // Find guests invited by this RSVP (match by userId or RSVP ID)
    const guests = rsvps.filter(r => 
      r.invitedBy === rsvp.userId || r.invitedBy === rsvp.id
    );

    if (isAdmin) {
      adminLog('Matched Inviter', invitedByRsvp ? {
        id: invitedByRsvp.id,
        name: invitedByRsvp.name,
        userId: invitedByRsvp.userId,
      } : null);
      adminGroupLog('Matched Guests', guests.map((guest) => ({
        id: guest.id,
        name: guest.name,
        userId: guest.userId,
        invitedBy: guest.invitedBy,
      })));
    }

    setGuestRelationships({
      invitedBy: invitedByRsvp,
      guests,
    });

    // Fetch invoice if it exists
    if (event && rsvp.invoiceId) {
      try {
        const invoiceDoc = await getDoc(
          doc(db, 'units', event.unitId, 'events', event.id, 'invoices', rsvp.invoiceId)
        );
        
        if (invoiceDoc.exists()) {
          const invoiceData = invoiceDoc.data();
          if (!isActiveInvoiceData(invoiceData)) {
            setInvoice(null);
          } else {
            setInvoice(mapFirestoreInvoiceData(invoiceDoc.id, invoiceData as Record<string, unknown>));
          }
        } else {
          setInvoice(null);
        }
      } catch (error) {
        console.error('Error fetching invoice:', error);
        setInvoice(null);
      }
    } else {
      setInvoice(null);
    }
  };

  const handleCloseDetail = () => {
    setSelectedRsvp(null);
    setInvoice(null);
    setGuestRelationships({ guests: [] });
  };

  const handleInvoiceUpdatedFromModal = useCallback((inv: Invoice) => {
    setInvoiceMap((prev) => ({ ...prev, [inv.id]: inv }));
    setInvoice((cur) => (cur?.id === inv.id ? inv : cur));
    setPaymentModalInvoice(inv);
    if (inv.status === 'paid' && inv.rsvps?.length) {
      const ids = new Set(inv.rsvps);
      setRsvps((prev) => prev.map((r) => (ids.has(r.id) ? { ...r, paid: true } : r)));
      setSelectedRsvp((sel) => (sel && ids.has(sel.id) ? { ...sel, paid: true } : sel));
    }
  }, []);

  const openPaymentsModal = useCallback(
    async (rsvp: RSVP) => {
      if (!event) {
        return;
      }
      try {
        const payer = getInvoicePayerRsvp(rsvp, rsvps);
        const invId = payer.invoiceId?.trim();
        let inv: Invoice | null = invId ? invoiceMap[invId] ?? null : null;

        if (invId && !inv) {
          const snap = await getDoc(
            doc(db, 'units', event.unitId, 'events', event.id, 'invoices', invId),
          );
          if (snap.exists()) {
            const data = snap.data();
            if (data && isActiveInvoiceData(data)) {
              inv = mapFirestoreInvoiceData(snap.id, data as Record<string, unknown>);
              setInvoiceMap((prev) => ({ ...prev, [inv!.id]: inv! }));
            }
          }
        }

        if (!inv) {
          const result = await ensureInvoiceForBooking({
            event,
            rsvps,
            viewerRsvp: rsvp,
            ownerUid: userProfile?.uid,
          });
          if (!result.ok) {
            showError(result.message);
            return;
          }
          const createdInv = result.invoice;
          inv = createdInv;
          setInvoiceMap((prev) => ({ ...prev, [createdInv.id]: createdInv }));
          if (result.created) {
            setRsvps((prev) =>
              prev.map((row) =>
                createdInv.rsvps?.includes(row.id)
                  ? { ...row, invoiceId: createdInv.id }
                  : row,
              ),
            );
          }
        }

        if (!inv) {
          showError('No invoice could be loaded for this booking.');
          return;
        }

        setPaymentModalInvoice(inv);
        setPaymentModalOpen(true);
      } catch (e) {
        console.error(e);
        showError('Could not open payments.');
      }
    },
    [event, rsvps, invoiceMap, userProfile?.uid],
  );

  const handleApprovePayment = useCallback(
    async (rsvp: RSVP) => {
      if (!event) return;
      const payer = getInvoicePayerRsvp(rsvp, rsvps);
      const invId = payer.invoiceId?.trim();
      const inv = invId ? invoiceMap[invId] ?? null : null;
      if (!inv || inv.status !== 'pending_verification') return;
      const outstanding = Math.max(0, Math.round((Number(inv.outstandingBalance) || 0) * 100) / 100);
      if (outstanding <= 0) return;

      const result = await confirmAction({
        title: 'Approve Bank Transfer',
        text: `Confirm the bank transfer of ${formatCurrency(outstanding)} has been received and approve this payment?`,
        confirmButtonText: 'Approve',
        cancelButtonText: 'Cancel',
      });
      if (!result.isConfirmed) return;

      setApprovingPaymentRsvpId(rsvp.id);
      try {
        const fn = httpsCallable(functions, 'recordManualInvoicePayment');
        const res = await fn({
          unitId: event.unitId,
          eventId: event.id,
          invoiceId: inv.id,
          amount: outstanding,
        });
        const data = res.data as { success?: boolean; invoice?: { status?: string; rsvps?: string[] } };
        if (data?.success) {
          showSuccess('Payment approved');
          if (data.invoice) {
            handleInvoiceUpdatedFromModal(data.invoice as any);
          }
        } else {
          showError('Unexpected response from server');
        }
      } catch (e: unknown) {
        const err = e as { message?: string };
        showError(err.message || 'Failed to approve payment');
      } finally {
        setApprovingPaymentRsvpId(null);
      }
    },
    [event, rsvps, invoiceMap, handleInvoiceUpdatedFromModal],
  );

  const placeCardUnitInfo = useMemo(() => {
    if (!event?.unitId) {
      return { name: '', number: '', logoUrl: undefined as string | undefined };
    }
    const u = units.find((x) => x.id === event.unitId);
    return {
      name: u?.name ?? '',
      number: u?.unitNumber ?? u?.settings?.defaultEventSettings?.unitId ?? '',
      logoUrl: u?.settings?.logoUrl ?? undefined,
    };
  }, [event?.unitId, units]);

  const handleGeneratePlaceCardForAttendee = useCallback(
    async (rsvp: RSVP) => {
      if (!event?.unitId) {
        showError('Could not determine the unit for this event.');
        return;
      }
      if (rsvp.apologies) {
        showError('Place cards are not generated for guests marked as apologies.');
        return;
      }
      if (rsvp.status === 'cancelled') {
        showError('Place cards are not generated for cancelled bookings.');
        return;
      }
      setGeneratingPlaceCardRsvpId(rsvp.id);
      try {
        const template = await loadEventPlaceCardTemplate(event.unitId, event);
        const result = await generateCustomPlaceCards([rsvp], event, placeCardTables, template, placeCardUnitInfo);
        const problem = describePlaceCardGenerationProblems(result);
        if (result.failedCards > 0) {
          showError(problem ?? 'Failed to generate place card');
        } else if (problem) {
          showWarning(`Place card downloaded for ${rsvp.name}, but ${problem.charAt(0).toLowerCase()}${problem.slice(1)}`);
        } else {
          showSuccess(`Place card downloaded for ${rsvp.name}`);
        }
      } catch (error) {
        console.error('Failed to generate place card', error);
        showError('Failed to generate place card');
      } finally {
        setGeneratingPlaceCardRsvpId(null);
      }
    },
    [event, placeCardTables, placeCardUnitInfo],
  );

  const handleToggleApologies = async (rsvp: RSVP) => {
    if (!event) return;

    const newApologyValue = !rsvp.apologies;
    const updateData: any = {
      apologies: newApologyValue,
      updatedAt: new Date(),
    };

    if (newApologyValue && rsvp.status !== 'declined') {
      updateData.status = 'declined';
    } else if (!newApologyValue && rsvp.apologies && rsvp.status === 'declined') {
      updateData.status = 'confirmed';
    }

    try {
      const rsvpRef = doc(db, 'units', event.unitId, 'events', event.id, 'rsvps', rsvp.id);
      await updateDoc(rsvpRef, updateData);

      const attendeeDelta = computeAttendeeDeltaForApologyToggle(rsvp, newApologyValue);

      // currentAttendees is recounted server-side (syncEventAttendeeCount trigger). Reflect it locally meanwhile.
      if (attendeeDelta !== 0) {
        setEvent({ ...event, currentAttendees: event.currentAttendees + attendeeDelta });
      }

      const updatedRsvp: RSVP = {
        ...rsvp,
        apologies: newApologyValue,
        status: updateData.status ?? rsvp.status,
        updatedAt: new Date(),
      };

      setRsvps(rsvps.map((item) => (item.id === rsvp.id ? updatedRsvp : item)));

      if (selectedRsvp?.id === rsvp.id) {
        setSelectedRsvp(updatedRsvp);
      }

      showSuccess(newApologyValue ? 'Marked as apologies' : 'Apology removed');
    } catch (error) {
      console.error('Error toggling apologies:', error);
      showError('Failed to update apologies status');
    }
  };

  const handleCreateApology = async () => {
    if (!event) return;

    if (!apologyForm.firstName.trim() || !apologyForm.lastName.trim() || !apologyForm.email.trim()) {
      showError('First name, last name, and email are required');
      return;
    }

    setCreatingApology(true);

    try {
      const now = new Date();
      const apologyData: any = {
        eventId: event.id,
        firstName: apologyForm.firstName.trim(),
        lastName: apologyForm.lastName.trim(),
        name: `${apologyForm.firstName.trim()} ${apologyForm.lastName.trim()}`,
        email: apologyForm.email.trim(),
        responses: {},
        status: 'declined',
        apologies: true,
        paid: false,
        checkedIn: false,
        plusOneAllowed: false,
        createdAt: now,
        updatedAt: now,
      };

      if (apologyForm.rank.trim()) {
        apologyData.rank = apologyForm.rank.trim();
        const rankShortName = getRankShortName(apologyForm.rank);
        if (rankShortName) {
          apologyData.rankShortName = rankShortName;
        }
      }
      if (apologyForm.additionalRank.trim()) {
        apologyData.additionalRank = apologyForm.additionalRank.trim();
      }
      if (apologyForm.unit.trim()) {
        apologyData.unit = apologyForm.unit.trim();
      }
      if (apologyForm.specialRequests.trim()) {
        apologyData.specialRequests = apologyForm.specialRequests.trim();
      }

      const rsvpRef = await addDoc(
        collection(db, 'units', event.unitId, 'events', event.id, 'rsvps'),
        apologyData,
      );

      const newRsvp: RSVP = {
        id: rsvpRef.id,
        eventId: event.id,
        userId: '',
        name: apologyData.name,
        firstName: apologyData.firstName,
        lastName: apologyData.lastName,
        rank: apologyData.rank || '',
        additionalRank: apologyData.additionalRank,
        unit: apologyData.unit || '',
        email: apologyData.email,
        responses: apologyData.responses,
        status: apologyData.status,
        invoiceId: '',
        paid: apologyData.paid,
        invitedBy: undefined,
        checkedIn: apologyData.checkedIn,
        plusOneAllowed: apologyData.plusOneAllowed,
        specialRequests: apologyData.specialRequests,
        createdAt: now,
        updatedAt: now,
        apologies: true,
      } as RSVP;

      setRsvps((prev) => [...prev, newRsvp]);
      showSuccess('Apology recorded successfully');
      setApologyForm({
        firstName: '',
        lastName: '',
        email: '',
        rank: '',
        additionalRank: '',
        unit: '',
        specialRequests: '',
      });
      setShowApologyModal(false);
    } catch (error) {
      console.error('Error recording apology:', error);
      showError('Failed to record apology. Please try again.');
    } finally {
      setCreatingApology(false);
    }
  };

  const handleCreateGuest = async () => {
    if (!event) return;

    if (!guestForm.firstName.trim() || !guestForm.lastName.trim() || !guestForm.email.trim()) {
      showError('First name, last name, and email are required');
      return;
    }

    // Validate ticket selection if tickets exist
    if (event.tickets && event.tickets.length > 0 && !guestForm.selectedTicketId) {
      showError('Please select a ticket type');
      return;
    }

    // Validate meal choices if ticket has courses
    if (guestForm.selectedTicketId && event.tickets) {
      const selectedTicket = event.tickets.find(t => t.id === guestForm.selectedTicketId);
      if (selectedTicket?.courses && selectedTicket.courses.length > 0) {
        const instanceIndex = 1;
        const courseSelections = guestForm.ticketCourseSelections[guestForm.selectedTicketId]?.[instanceIndex] || {};
        const missingCourses = selectedTicket.courses.filter(
          (course) => !isMealCourseSelectionComplete(courseSelections[course.id]),
        );
        if (missingCourses.length > 0) {
          showError(`Please select meal options for: ${missingCourses.map(c => c.name).join(', ')}`);
          return;
        }
      }
    }

    // Validate meal choices for events with courses (no tickets)
    if (!event.tickets && event.courses && event.courses.length > 0) {
      const missingCourses = event.courses.filter(
        (course) => !isMealCourseSelectionComplete(guestForm.courseSelections[course.name]),
      );
      if (missingCourses.length > 0) {
        showError(`Please select meal options for: ${missingCourses.map(c => c.name).join(', ')}`);
        return;
      }
    }

    setCreatingGuest(true);

    try {
      const now = new Date();
      const responses: Record<string, any> = {};

      // Handle tickets and meal choices
      if (event.tickets && event.tickets.length > 0 && guestForm.selectedTicketId) {
        const selectedTicket = event.tickets.find(t => t.id === guestForm.selectedTicketId);
        if (selectedTicket) {
          responses.Tickets = [{
            id: selectedTicket.id,
            name: selectedTicket.name,
            price: selectedTicket.price || 0,
            qty: 1,
          }];

          // Add meal course selections if they exist
          const instanceIndex = 1;
          const courseSelections = guestForm.ticketCourseSelections[guestForm.selectedTicketId]?.[instanceIndex];
          if (courseSelections && Object.keys(courseSelections).length > 0) {
            responses.TicketCourses = {
              [guestForm.selectedTicketId]: {
                [instanceIndex]: courseSelections,
              },
            };
          }
        }
      } else if (event.courses && event.courses.length > 0) {
        // Handle legacy course structure (no tickets)
        event.courses.forEach((course) => {
          const selection = guestForm.courseSelections[course.name];
          if (selection && selection.trim()) {
            responses[course.name] = selection.trim();
          }
        });
      }

      const guestData: any = {
        eventId: event.id,
        firstName: guestForm.firstName.trim(),
        lastName: guestForm.lastName.trim(),
        name: `${guestForm.firstName.trim()} ${guestForm.lastName.trim()}`,
        email: guestForm.email.trim(),
        responses: responses,
        status: 'confirmed',
        apologies: false,
        paid: false,
        checkedIn: false,
        plusOneAllowed: false,
        createdAt: now,
        updatedAt: now,
      };

      if (guestForm.rank.trim()) {
        guestData.rank = guestForm.rank.trim();
        const rankShortName = getRankShortName(guestForm.rank);
        if (rankShortName) {
          guestData.rankShortName = rankShortName;
        }
      }
      if (guestForm.additionalRank.trim()) {
        guestData.additionalRank = guestForm.additionalRank.trim();
      }
      if (guestForm.unit.trim()) {
        guestData.unit = guestForm.unit.trim();
      }
      if (guestForm.specialRequests.trim()) {
        guestData.specialRequests = guestForm.specialRequests.trim();
      }
      if (guestForm.invitedBy.trim()) {
        guestData.invitedBy = guestForm.invitedBy.trim();
      }

      Object.assign(guestData, buildGuestRsvpCreateEmailFlags(guestForm.notifyGuestByEmail));

      const rsvpRef = await addDoc(
        collection(db, 'units', event.unitId, 'events', event.id, 'rsvps'),
        guestData,
      );

      const newRsvp: RSVP = {
        id: rsvpRef.id,
        eventId: event.id,
        userId: '',
        name: guestData.name,
        firstName: guestData.firstName,
        lastName: guestData.lastName,
        rank: guestData.rank || '',
        additionalRank: guestData.additionalRank,
        unit: guestData.unit || '',
        email: guestData.email,
        responses: guestData.responses,
        status: guestData.status,
        invoiceId: '',
        paid: guestData.paid,
        invitedBy: guestData.invitedBy,
        checkedIn: guestData.checkedIn,
        plusOneAllowed: guestData.plusOneAllowed,
        specialRequests: guestData.specialRequests,
        createdAt: now,
        updatedAt: now,
        apologies: false,
      } as RSVP;

      // currentAttendees is recounted server-side (syncEventAttendeeCount trigger).
      setEvent({ ...event, currentAttendees: event.currentAttendees + 1 });

      setRsvps((prev) => [...prev, newRsvp]);
      showSuccess('Guest added successfully');
      setGuestForm({
        firstName: '',
        lastName: '',
        email: '',
        rank: '',
        additionalRank: '',
        unit: '',
        specialRequests: '',
        invitedBy: '',
        selectedTicketId: '',
        ticketCourseSelections: {},
        courseSelections: {},
        notifyGuestByEmail: false,
      });
      setShowGuestModal(false);
    } catch (error) {
      console.error('Error adding guest:', error);
      showError('Failed to add guest. Please try again.');
    } finally {
      setCreatingGuest(false);
    }
  };

  const handleEditRsvp = (rsvp: RSVP) => {
    const responses = rsvp.responses || {};
    const normalizedResponses: Record<string, any> = { ...responses };

    if (event?.tickets && event.tickets.length > 0) {
      const ticketsArray = Array.isArray(responses.Tickets) ? [...responses.Tickets] : [];
      const hasTickets = ticketsArray.length > 0;

      if (!hasTickets) {
        normalizedResponses.Tickets = [
          {
            id: '',
            name: '',
            price: 0,
            qty: 1,
          },
        ];
      } else {
        normalizedResponses.Tickets = ticketsArray.map((ticket) => ({
          id: ticket.id || '',
          name: ticket.name || '',
          price: ticket.price ?? 0,
          qty: ticket.qty ?? 1,
        }));
      }

      // Normalise to Format A (single canonical format) and ensure all event-ticket courses have keys
      let normalizedTicketCourses = normalizeTicketCoursesToFormatA(
        responses.TicketCourses
      );
      event.tickets.forEach((ticket) => {
        if (ticket.courses && ticket.courses.length > 0) {
          const ticketSelection =
            normalizedResponses.Tickets?.find((selectedTicket: any) => selectedTicket.id === ticket.id) || null;
          if (!ticketSelection) {
            const minimalEntry = normalizedResponses.Tickets?.find((selectedTicket: any) => !selectedTicket.id);
            if (minimalEntry) {
              minimalEntry.id = ticket.id;
              minimalEntry.name = ticket.name;
              minimalEntry.price = ticket.price ?? 0;
            }
          }
          const existing = normalizedTicketCourses[ticket.id]?.[1] ?? {};
          const filled: Record<string, string> = { ...existing };
          ticket.courses.forEach((course) => {
            if (!(course.id in filled)) filled[course.id] = '';
          });
          normalizedTicketCourses = {
            ...normalizedTicketCourses,
            [ticket.id]: { 1: filled },
          };
        }
      });
      if (Object.keys(normalizedTicketCourses).length > 0) {
        normalizedResponses.TicketCourses = normalizedTicketCourses;
      } else {
        delete normalizedResponses.TicketCourses;
      }
    } else if (event?.courses && event.courses.length > 0) {
      normalizedResponses.Tickets = Array.isArray(responses.Tickets) ? responses.Tickets : undefined;

      event.courses.forEach((course) => {
        // Only set to empty string if the key doesn't exist in responses
        // This preserves existing course selections
        if (!(course.name in normalizedResponses)) {
          normalizedResponses[course.name] = '';
        }
      });
    }

    setEditingRsvp(rsvp);
    
    // Combine specialRequests and allergies/dietary requirements from responses
    const specialRequestsValue = rsvp.specialRequests || '';
    const allergiesValue = rsvp.responses?.Allergies || '';
    const dietaryValue = Array.isArray(rsvp.responses?.Dietary) 
      ? rsvp.responses.Dietary.join(', ') 
      : rsvp.responses?.Dietary || '';
    
    // Combine all dietary-related information
    const combinedSpecialRequests = [
      specialRequestsValue,
      allergiesValue,
      dietaryValue
    ].filter(Boolean).join('; ').trim();
    
    let firstName = (rsvp.firstName ?? '').trim();
    let lastName = (rsvp.lastName ?? '').trim();
    if (!firstName && !lastName) {
      const full = (rsvp.name ?? '').trim();
      const spaceIdx = full.indexOf(' ');
      if (spaceIdx === -1) {
        firstName = full;
      } else {
        firstName = full.slice(0, spaceIdx).trim();
        lastName = full.slice(spaceIdx + 1).trim();
      }
    }

    setEditForm({
      firstName,
      lastName,
      email: rsvp.email || '',
      rank: rsvp.rank || '',
      additionalRank: rsvp.additionalRank || '',
      unit: rsvp.unit || '',
      specialRequests: combinedSpecialRequests || specialRequestsValue || allergiesValue || dietaryValue || '',
      responses: normalizedResponses,
      apologies: !!rsvp.apologies,
      status: rsvp.status || 'confirmed',
      invitedBy: rsvp.invitedBy || '',
      inviterPaysFor: rsvp.inviterPaysFor !== false,
      notifyGuestByEmail: false,
    });
  };

  const handleCancelEdit = () => {
    setEditingRsvp(null);
    setEditForm({
      firstName: '',
      lastName: '',
      email: '',
      rank: '',
      additionalRank: '',
      unit: '',
      specialRequests: '',
      responses: {},
      apologies: false,
      status: 'confirmed',
      invitedBy: '',
      inviterPaysFor: true,
      notifyGuestByEmail: false,
    });
  };

  const handleSaveEdit = async () => {
    if (!editingRsvp || !event) return;

    // Basic validation
    const trimmedFirstName = editForm.firstName.trim();
    const trimmedLastName = editForm.lastName.trim();
    const fullName = `${trimmedFirstName} ${trimmedLastName}`.trim();
    if (!trimmedFirstName || !trimmedLastName) {
      showError('First name and last name are required');
      return;
    }
    // Validate ticket course selections - prevent silently clearing courses on save
    if (event.tickets && event.tickets.length > 0 && !editForm.apologies) {
      const selectedTickets = Array.isArray(editForm.responses.Tickets)
        ? (editForm.responses.Tickets as any[]).filter((t) => t && t.id)
        : [];
      for (const ticketEntry of selectedTickets) {
        const ticketType = event.tickets.find((t) => t.id === ticketEntry.id);
        if (ticketType?.courses && ticketType.courses.length > 0) {
          const tc = (editForm.responses as any).TicketCourses || {};
          const instanceCourses = tc?.[ticketType.id]?.[1] || {};
          for (const course of ticketType.courses) {
            if (!isMealCourseSelectionComplete(instanceCourses[course.id])) {
              showError(`Please select an option for ${course.name}`);
              return;
            }
          }
        }
      }
    }

    try {
      const rsvpRef = doc(db, 'units', event.unitId, 'events', event.id, 'rsvps', editingRsvp.id);
      
      // Clean responses object - remove undefined values from Tickets array and TicketCourses
      const cleanedResponses = { ...editForm.responses };
      if (Array.isArray(cleanedResponses.Tickets)) {
        cleanedResponses.Tickets = cleanedResponses.Tickets
          .map((t: any) => ({
            id: t.id,
            name: t.name,
            price: t.price || 0,
            // Multi-tenant carts can hold a real qty of 0..N per ticket type; meal-choice entries are always qty 1.
            qty: Number(t.qty) || 0,
          }))
          .filter((t: any) => t.id && t.name && t.qty > 0);
      }
      // Clean TicketCourses to remove undefined values
      if (cleanedResponses.TicketCourses) {
        const cleanedCourses: any = {};
        Object.keys(cleanedResponses.TicketCourses).forEach((ticketId) => {
          const ticketMap: any = {};
          Object.keys(cleanedResponses.TicketCourses[ticketId]).forEach((instanceIndex) => {
            const instanceMap: any = {};
            Object.keys(cleanedResponses.TicketCourses[ticketId][instanceIndex]).forEach((courseId) => {
              const value = cleanedResponses.TicketCourses[ticketId][instanceIndex][courseId];
              if (value !== undefined && value !== null && value !== '') {
                instanceMap[courseId] = value;
              }
            });
            if (Object.keys(instanceMap).length > 0) {
              ticketMap[instanceIndex] = instanceMap;
            }
          });
          if (Object.keys(ticketMap).length > 0) {
            cleanedCourses[ticketId] = ticketMap;
          }
        });
        cleanedResponses.TicketCourses = cleanedCourses;
      }

      const normalizedRank = editForm.rank?.trim() || '';
      const normalizedAdditionalRank = editForm.additionalRank?.trim() || '';
      const normalizedUnit = editForm.unit?.trim() || '';
      const normalizedSpecialRequests = editForm.specialRequests?.trim() || '';
      const normalizedInvitedBy = editForm.invitedBy?.trim() || '';

      // Keep dietary fields in sync with the combined editor (matches InvitationResponsePage / RSVPPage)
      cleanedResponses.Allergies = normalizedSpecialRequests;
      // Null removes legacy Dietary from Firestore map merge when data was consolidated into the textarea
      cleanedResponses.Dietary = null;

      const updateData: any = {
        name: fullName,
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        email: editForm.email.trim(),
        rank: normalizedRank || null,
        unit: normalizedUnit || null,
        specialRequests: normalizedSpecialRequests || null,
        responses: cleanedResponses,
        apologies: editForm.apologies || false,
        paid: editingRsvp.paid,
        updatedAt: new Date(),
        invitedBy: normalizedInvitedBy || null,
        inviterPaysFor: normalizedInvitedBy ? editForm.inviterPaysFor : null,
        // Always persist additionalRank explicitly so it can be cleared
        additionalRank: normalizedAdditionalRank || null,
      };
      
      // Find the rank object to get shortName, or clear it if no rank
      if (normalizedRank) {
        updateData.rankShortName = getRankShortName(normalizedRank);
      } else {
        updateData.rankShortName = null;
      }

      updateData.status = editForm.status;

      Object.assign(updateData, buildGuestRsvpEditEmailFlags(editForm.notifyGuestByEmail));

      // Remove undefined values (Firestore doesn't allow undefined)
      const cleanedData = Object.fromEntries(
        Object.entries(updateData).filter(([_, v]) => v !== undefined)
      );

      const prevInvitedBy =
        typeof editingRsvp.invitedBy === 'string' ? editingRsvp.invitedBy.trim() : '';
      const prevInviterPays = editingRsvp.inviterPaysFor !== false;
      const paymentRelationshipChanged =
        prevInvitedBy !== normalizedInvitedBy ||
        (normalizedInvitedBy.length > 0 && prevInviterPays !== editForm.inviterPaysFor);

      await updateDoc(rsvpRef, cleanedData);

      // Update local state
      const updatedStatus = updateData.status ?? editingRsvp.status;

      let updatedRsvp: RSVP = {
        ...editingRsvp,
        ...editForm,
        name: fullName,
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        rank: normalizedRank || '',
        additionalRank: normalizedAdditionalRank || '',
        unit: normalizedUnit || '',
        specialRequests: normalizedSpecialRequests || '',
        responses: cleanedResponses,
        status: updatedStatus,
        updatedAt: new Date(),
        invitedBy: normalizedInvitedBy || undefined,
        inviterPaysFor: normalizedInvitedBy ? editForm.inviterPaysFor : undefined,
        rankShortName: updateData.rankShortName ?? undefined,
      };

      const nextRsvps = rsvps.map((r) => (r.id === editingRsvp.id ? updatedRsvp : r));

      if (paymentRelationshipChanged && (prevInvitedBy || normalizedInvitedBy)) {
        const updatedInvoices = await recalcHostInvoicesAfterGuestPaymentEdit({
          unitId: event.unitId,
          eventId: event.id,
          event,
          previousGuest: editingRsvp,
          updatedGuest: updatedRsvp,
          allRsvps: nextRsvps,
          resolveInvoice: (invoiceId) => invoiceMap[invoiceId] ?? null,
        });
        if (updatedInvoices.length > 0) {
          setInvoiceMap((prev) => {
            const next = { ...prev };
            for (const inv of updatedInvoices) {
              next[inv.id] = inv;
            }
            return next;
          });
        }
        const guestOnHostBill =
          normalizedInvitedBy.length > 0 && editForm.inviterPaysFor;
        const newHost = nextRsvps.find((r) => {
          const uid = typeof r.userId === 'string' ? r.userId.trim() : '';
          return uid === normalizedInvitedBy || r.id === normalizedInvitedBy;
        });
        updatedRsvp = {
          ...updatedRsvp,
          invoiceId: guestOnHostBill && newHost?.invoiceId ? newHost.invoiceId : '',
          ...(guestOnHostBill ? {} : { paid: false }),
        };
      }

      // Re-price the paying invoice against payments already made so paid/partial status follows the new total.
      // Server-side: stewards can't write invoices directly. Invoice + RSVP listeners pick up the result.
      const priceChanged =
        paymentRelationshipChanged ||
        Boolean(editingRsvp.apologies) !== Boolean(updatedRsvp.apologies) ||
        editingRsvp.status !== updatedRsvp.status ||
        Math.abs(
          computeRsvpSubtotalForInvoice(editingRsvp, event) - computeRsvpSubtotalForInvoice(updatedRsvp, event),
        ) > 0.005;
      let recalcFailed = false;
      let repricedPaid: { ids: Set<string>; paid: boolean } | null = null;
      if (priceChanged) {
        try {
          const res = await httpsCallable(functions, 'recalcRsvpInvoice')({
            unitId: event.unitId,
            eventId: event.id,
            rsvpId: editingRsvp.id,
          });
          const data = res.data as { updated?: boolean; status?: string; rsvps?: string[] };
          if (data?.updated && Array.isArray(data.rsvps)) {
            repricedPaid = { ids: new Set(data.rsvps), paid: data.status === 'paid' };
          }
        } catch (recalcError) {
          console.error('Error recalculating invoice after RSVP edit:', recalcError);
          recalcFailed = true;
        }
      }
      // Don't let the pre-save paid flag overwrite what the re-price (and its snapshot) just set.
      if (repricedPaid?.ids.has(updatedRsvp.id)) {
        updatedRsvp = { ...updatedRsvp, paid: repricedPaid.paid };
      }

      setRsvps(
        nextRsvps.map((r) => {
          const row = r.id === editingRsvp.id ? updatedRsvp : r;
          return repricedPaid?.ids.has(row.id) ? { ...row, paid: repricedPaid.paid } : row;
        }),
      );

      if (selectedRsvp?.id === editingRsvp.id) {
        setSelectedRsvp(updatedRsvp);
      }

      if (event) {
        const attendeeDelta = computeAttendeeDeltaForApologyToggle(
          editingRsvp,
          Boolean(editForm.apologies),
        );

        // currentAttendees is recounted server-side (syncEventAttendeeCount trigger).
        if (attendeeDelta !== 0) {
          setEvent({ ...event, currentAttendees: event.currentAttendees + attendeeDelta });
        }
      }

      if (recalcFailed) {
        showWarning('RSVP saved, but the invoice total could not be updated. Please check their payment status.');
      } else {
        showSuccess('RSVP updated successfully!');
      }
      handleCancelEdit();
    } catch (error) {
      console.error('Error updating RSVP:', error);
      showError('Failed to update RSVP');
    }
  };

  const handleDeleteRsvp = async (rsvp: RSVP) => {
    if (!event) return;

    const isInvoiceLinked =
      typeof rsvp.invoiceId === 'string' && rsvp.invoiceId.trim() !== '';

    const result = await confirmAction({
      title: 'Delete RSVP?',
      text: `Are you sure you want to remove ${rsvp.name}'s RSVP?${
        isInvoiceLinked
          ? ' If they have an invoice, it will be voided or unlinked (records are kept for recovery, not permanently erased).'
          : ''
      }`,
      confirmButtonText: 'Yes, delete it',
    });

    if (result.isConfirmed && event) {
      try {
        const deleteRsvpSafe = httpsCallable(functions, 'deleteRsvpSafe');
        await deleteRsvpSafe({
          unitId: event.unitId,
          eventId: event.id,
          rsvpId: rsvp.id,
        });

        setRsvps(rsvps.filter(r => r.id !== rsvp.id));
        // Server now adjusts attendee count correctly; refetch event to avoid drift
        try {
          const refreshedEvent = await getDoc(doc(db, 'units', event.unitId, 'events', event.id));
          if (refreshedEvent.exists()) {
            setEvent(mapEventData(refreshedEvent, event.unitId));
          }
        } catch (e) {
          // best-effort
        }

        if (selectedRsvp?.id === rsvp.id) {
          handleCloseDetail();
        }

        showSuccess('RSVP deleted successfully!');
      } catch (error) {
        console.error('Error deleting RSVP:', error);
        showError('Failed to delete RSVP');
      }
    }
  };

  const handleCopyDashboardLink = async (rsvp: RSVP) => {
    if (!event) return;
    
    try {
      // Generate a link to the specific RSVP dashboard
      const rsvpDashboardUrl = `${window.location.origin}/unit/${event.unitId}/event/${event.id}/rsvp/${rsvp.id}/dashboard`;
      await navigator.clipboard.writeText(rsvpDashboardUrl);
      showSuccess(`RSVP dashboard link copied for ${rsvp.name}`);
    } catch (error) {
      console.error('Error copying dashboard link:', error);
      showError('Failed to copy dashboard link');
    }
  };

  const exportableRsvps = useMemo(() => {
    if (exportFilter === 'attending') {
      return rsvps.filter(isAttendeeCountingRsvp);
    }

    if (exportFilter === 'apologies') {
      return rsvps.filter((rsvp) => rsvp.apologies);
    }

    return rsvps;
  }, [exportFilter, rsvps]);

  const exportFilterLabel = useMemo(() => {
    switch (exportFilter) {
      case 'attending':
        return 'Attending only';
      case 'apologies':
        return 'Apologies only';
      default:
        return 'All RSVPs';
    }
  }, [exportFilter]);

  const prefixFilterOptions = useMemo((): MultiSelectOption[] => {
    const counts = new Map<string, number>();
    for (const r of rsvps) {
      const key = (r.rank ?? '').trim() || PREFIX_FILTER_NONE;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }

    const entries: MultiSelectOption[] = [];
    const seen = new Set<string>();

    const push = (value: string, label: string) => {
      if (seen.has(value)) {
        return;
      }
      seen.add(value);
      entries.push({
        value,
        label,
        count: counts.get(value),
      });
    };

    push(PREFIX_FILTER_NONE, 'No prefix');

    for (const p of unitTypePrefixes) {
      const name = p.name?.trim();
      if (!name) {
        continue;
      }
      push(name, p.shortName ? `${name} (${p.shortName})` : name);
    }

    for (const r of rsvps) {
      const name = (r.rank ?? '').trim();
      if (!name) {
        continue;
      }
      if (!seen.has(name)) {
        push(name, name);
      }
    }

    return entries.sort((a, b) => {
      if (a.value === PREFIX_FILTER_NONE) {
        return -1;
      }
      if (b.value === PREFIX_FILTER_NONE) {
        return 1;
      }
      return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
    });
  }, [unitTypePrefixes, rsvps]);

  const rsvpsForTable = useMemo(() => {
    if (prefixFilterSelection.length === 0) {
      return rsvps;
    }
    const allowed = new Set(prefixFilterSelection);
    return rsvps.filter((r) => {
      const key = (r.rank ?? '').trim() || PREFIX_FILTER_NONE;
      return allowed.has(key);
    });
  }, [rsvps, prefixFilterSelection]);

  /** Export filter (status) plus prefix selection, matching what the list is narrowed to when prefixes are chosen */
  const exportableRsvpsWithCurrentFilters = useMemo(() => {
    let list =
      exportFilter === 'attending'
        ? rsvps.filter(isAttendeeCountingRsvp)
        : exportFilter === 'apologies'
          ? rsvps.filter((rsvp) => rsvp.apologies)
          : rsvps;

    if (prefixFilterSelection.length > 0) {
      const allowed = new Set(prefixFilterSelection);
      list = list.filter((r) => {
        const key = (r.rank ?? '').trim() || PREFIX_FILTER_NONE;
        return allowed.has(key);
      });
    }

    return list;
  }, [rsvps, exportFilter, prefixFilterSelection]);

  const currentFiltersExportSummary = useMemo(() => {
    const parts: string[] = [exportFilterLabel];
    if (prefixFilterSelection.length > 0) {
      parts.push(
        `${prefixFilterSelection.length} selected prefix${prefixFilterSelection.length === 1 ? '' : 'es'}`,
      );
    } else {
      parts.push('all prefixes');
    }
    return parts.join(', ');
  }, [exportFilterLabel, prefixFilterSelection]);

  const handleExportPDF = async () => {
    if (!event) {
      showError('No event found to export');
      return;
    }

    if (event.unitId) {
      try {
        const gate = httpsCallable(functions, 'assertUnitExportAllowed');
        await gate({ unitId: event.unitId });
      } catch (err: unknown) {
        const details =
          err && typeof err === 'object' && 'details' in err
            ? (err as { details?: { code?: string } }).details
            : undefined;
        if (details?.code === 'CREDIT_DEBT') {
          showError(
            'Exports are paused until the unit credit balance is brought back to zero or above. Please ask your secretary or treasurer to top up.',
          );
          return;
        }
        console.error(err);
        showError('Could not verify export eligibility.');
        return;
      }
    }

    if (exportableRsvps.length === 0) {
      showError('No RSVPs to export for the selected filter');
      return;
    }

    try {
      await generateAttendanceList(exportableRsvps, event, tableAssignments, invoiceMap, rsvps);
      showSuccess(`PDF exported successfully (${exportFilterLabel})`);
      setShowExportMenu(false);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      showError('Failed to export PDF');
    }
  };

  const handleExportCSV = async () => {
    if (!event) {
      showError('No event found to export');
      return;
    }

    if (event.unitId) {
      try {
        const gate = httpsCallable(functions, 'assertUnitExportAllowed');
        await gate({ unitId: event.unitId });
      } catch (err: unknown) {
        const details =
          err && typeof err === 'object' && 'details' in err
            ? (err as { details?: { code?: string } }).details
            : undefined;
        if (details?.code === 'CREDIT_DEBT') {
          showError(
            'Exports are paused until the unit credit balance is brought back to zero or above. Please ask your secretary or treasurer to top up.',
          );
          return;
        }
        console.error(err);
        showError('Could not verify export eligibility.');
        return;
      }
    }

    if (exportableRsvps.length === 0) {
      showError('No RSVPs to export for the selected filter');
      return;
    }

    try {
      // Fetch unit to get province
      let unitProvince = '';
      if (event.unitId) {
        try {
          const unitDoc = await getDoc(doc(db, 'units', event.unitId));
          if (unitDoc.exists()) {
            unitProvince = unitDoc.data()?.province || '';
          }
        } catch (e) {
          console.warn('Failed to fetch unit province for CSV export', e);
        }
      }
      
      exportToCSV(exportableRsvps, event, tableAssignments, unitProvince, invoiceMap, rsvps);
      showSuccess(`CSV exported successfully (${exportFilterLabel})`);
      setShowExportMenu(false);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      showError('Failed to export CSV');
    }
  };

  const handleExportPDFWithCurrentFilters = async () => {
    if (!event) {
      showError('No event found to export');
      return;
    }

    if (event.unitId) {
      try {
        const gate = httpsCallable(functions, 'assertUnitExportAllowed');
        await gate({ unitId: event.unitId });
      } catch (err: unknown) {
        const details =
          err && typeof err === 'object' && 'details' in err
            ? (err as { details?: { code?: string } }).details
            : undefined;
        if (details?.code === 'CREDIT_DEBT') {
          showError(
            'Exports are paused until the unit credit balance is brought back to zero or above. Please ask your secretary or treasurer to top up.',
          );
          return;
        }
        console.error(err);
        showError('Could not verify export eligibility.');
        return;
      }
    }

    if (exportableRsvpsWithCurrentFilters.length === 0) {
      showError('No RSVPs match the current filters');
      return;
    }

    try {
      await generateAttendanceList(exportableRsvpsWithCurrentFilters, event, tableAssignments, invoiceMap, rsvps);
      showSuccess(`PDF exported successfully (${currentFiltersExportSummary})`);
      setShowExportMenu(false);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      showError('Failed to export PDF');
    }
  };

  const handleExportCSVWithCurrentFilters = async () => {
    if (!event) {
      showError('No event found to export');
      return;
    }

    if (event.unitId) {
      try {
        const gate = httpsCallable(functions, 'assertUnitExportAllowed');
        await gate({ unitId: event.unitId });
      } catch (err: unknown) {
        const details =
          err && typeof err === 'object' && 'details' in err
            ? (err as { details?: { code?: string } }).details
            : undefined;
        if (details?.code === 'CREDIT_DEBT') {
          showError(
            'Exports are paused until the unit credit balance is brought back to zero or above. Please ask your secretary or treasurer to top up.',
          );
          return;
        }
        console.error(err);
        showError('Could not verify export eligibility.');
        return;
      }
    }

    if (exportableRsvpsWithCurrentFilters.length === 0) {
      showError('No RSVPs match the current filters');
      return;
    }

    try {
      let unitProvince = '';
      if (event.unitId) {
        try {
          const unitDoc = await getDoc(doc(db, 'units', event.unitId));
          if (unitDoc.exists()) {
            unitProvince = unitDoc.data()?.province || '';
          }
        } catch (e) {
          console.warn('Failed to fetch unit province for CSV export', e);
        }
      }

      exportToCSV(exportableRsvpsWithCurrentFilters, event, tableAssignments, unitProvince, invoiceMap, rsvps);
      showSuccess(`CSV exported successfully (${currentFiltersExportSummary})`);
      setShowExportMenu(false);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      showError('Failed to export CSV');
    }
  };

  const attendanceTableVisibilityDefaults = useMemo<VisibilityState>(
    () => ({ ticketNamesSearch: false }),
    [],
  );

  // Helper function to truncate text (must be before early returns for hooks)
  const truncateText = (text: string, maxLength: number = 25): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // IMPORTANT: This useMemo hook MUST be called before any early returns
  // to comply with React's Rules of Hooks
  const columns = useMemo<ColumnDef<RSVP>[]>(() => {
    // Return empty array during loading or when no event to prevent errors
    if (!event) return [];

    const questionAnswerColumns: ColumnDef<RSVP>[] = event.questionnaire?.length
      ? [
          {
            id: 'questionAnswers',
            accessorFn: (row) => formatRsvpQuestionAnswers(row, event),
            header: 'Additional Questions',
            enableSorting: false,
            cell: ({ row }) => {
              const rsvp = row.original;
              if (rsvp.apologies || rsvp.status === 'cancelled') {
                return <span className="text-xs italic text-muted-foreground">Not applicable</span>;
              }
              const answers = getRsvpQuestionAnswers(rsvp, event);
              if (answers.length === 0) {
                return <span className="text-xs italic text-muted-foreground">No answers</span>;
              }
              return (
                <div className="space-y-0.5 break-words text-xs text-muted-foreground">
                  {answers.map(({ question, answer }) => (
                    <div key={question}>
                      <span className="font-bold">{question}:</span> {answer}
                    </div>
                  ))}
                </div>
              );
            },
          },
        ]
      : [];

    return [
      {
        accessorKey: 'name',
        header: 'Name',
        enableSorting: true,
        cell: ({ row }) => {
          const rsvp = row.original;
          return (
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-base font-semibold text-foreground">
                {rsvp.name}
                {rsvp.userId && typeof rsvp.userId === 'string' && rsvp.userId.trim().length > 0 && (
                  <span title="Has account on Masonic Tools">
                    <User className="h-4 w-4 text-blue-600" />
                  </span>
                )}
              </div>
              {rsvp.email && (
                <a
                  href={`mailto:${rsvp.email}`}
                  className="md:hidden block max-w-[min(100%,16rem)] truncate text-xs text-primary underline-offset-2 hover:underline"
                >
                  {rsvp.email}
                </a>
              )}
              {(rsvp.rankShortName || rsvp.rank) && (
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {rsvp.rankShortName || rsvp.rank}
                </div>
              )}
              {rsvp.additionalRank && (
                <div className="text-xs text-muted-foreground">
                  {rsvp.additionalRank}
                </div>
              )}
              {rsvp.specialRequests && (
                <div className="mt-2 rounded bg-muted px-2 py-1 text-[11px] text-muted-foreground">
                  Note: {rsvp.specialRequests}
                </div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'email',
        header: 'Email',
        enableSorting: true,
        cell: ({ row }) => (
          <div className="text-sm font-medium text-foreground">{row.original.email}</div>
        ),
      },
      {
        accessorKey: 'unit',
        header: 'Unit',
        enableSorting: true,
        cell: ({ row }) => (
          <div className="text-sm text-muted-foreground">
            {row.original.unit || <span className="italic text-xs text-muted-foreground">Not provided</span>}
          </div>
        ),
      },
      {
        id: 'ticketNamesSearch',
        accessorFn: (row) => getRsvpTicketNamesForSearch(row, event),
        header: () => null,
        enableSorting: false,
        cell: () => null,
      },
      {
        id: 'relationships',
        header: 'Guest Relationships',
        enableSorting: false,
        cell: ({ row }) => {
          const rsvp = row.original;
          
          const invitedByValue = typeof rsvp.invitedBy === 'string' && rsvp.invitedBy.trim().length > 0
            ? rsvp.invitedBy.trim()
            : undefined;

          const invitedByRsvp = invitedByValue
            ? rsvps.find((candidate) => {
                const candidateUserId = typeof candidate.userId === 'string' ? candidate.userId.trim() : '';
                return candidateUserId === invitedByValue || candidate.id === invitedByValue;
              })
            : undefined;

          const hostIdentifiers = [
            typeof rsvp.userId === 'string' && rsvp.userId.trim().length > 0 ? rsvp.userId.trim() : undefined,
            rsvp.id,
          ].filter((identifier): identifier is string => typeof identifier === 'string' && identifier.length > 0);

          const invitedGuests = rsvps.filter((guest) => {
            const guestInvitedBy =
              typeof guest.invitedBy === 'string' && guest.invitedBy.trim().length > 0
                ? guest.invitedBy.trim()
                : undefined;
            if (!guestInvitedBy) {
              return false;
            }
            return hostIdentifiers.includes(guestInvitedBy);
          });
          
          const isHostExpanded = expandedHosts[rsvp.id] ?? false;

          return (
            <div className="space-y-1">
              {invitedByRsvp && (
                <div className="flex items-start gap-1 text-xs text-muted-foreground">
                  <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-primary" />
                  <span className="flex items-center gap-1">
                    <span className="font-medium text-foreground">Guest of:</span>{' '}
                    <span>{invitedByRsvp.name}</span>
                    {invitedByRsvp.userId && typeof invitedByRsvp.userId === 'string' && invitedByRsvp.userId.trim().length > 0 && (
                      <span title="Has account on Masonic Tools">
                        <User className="h-3 w-3 text-blue-600" />
                      </span>
                    )}
                  </span>
                </div>
              )}
              {invitedGuests.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-foreground">
                      Hosts {invitedGuests.length} guest{invitedGuests.length > 1 ? 's' : ''}
                    </span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="flex items-center gap-1 h-7 px-2 text-xs"
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedHosts((prev) => ({
                              ...prev,
                              [rsvp.id]: !isHostExpanded,
                            }));
                          }}
                          aria-label={isHostExpanded ? 'Hide guest list' : 'Show guest list'}
                        >
                          <span>{isHostExpanded ? 'Hide' : 'Show'}</span>
                          <ChevronDown
                            className={`h-3 w-3 transition-transform ${isHostExpanded ? 'rotate-180' : ''}`}
                          />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        {isHostExpanded ? 'Hide guest list' : 'Show guest list'}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  {isHostExpanded && (
                    <div className="mt-2 space-y-0.5 rounded-md border border-muted px-2 py-2 bg-background">
                      {invitedGuests.map((guest) => (
                        <div key={guest.id} className="flex items-center gap-2 text-xs">
                          <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground" />
                          <span className="flex items-center gap-1">
                            {guest.name}
                            {guest.userId && typeof guest.userId === 'string' && guest.userId.trim().length > 0 && (
                              <span title="Has account on Masonic Tools">
                                <User className="h-3 w-3 text-blue-600" />
                              </span>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {!invitedByRsvp && invitedGuests.length === 0 && (
                <span className="text-xs italic text-muted-foreground">No relationships</span>
              )}
            </div>
          );
        },
      },
      {
        id: 'table',
        header: 'Table',
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const tableA = tableAssignments[rowA.original.id]?.tableName || '';
          const tableB = tableAssignments[rowB.original.id]?.tableName || '';
          return tableA.localeCompare(tableB);
        },
        cell: ({ row }) => {
          const rsvp = row.original;
          const tableInfo = tableAssignments[rsvp.id];
          const isApology = !!rsvp.apologies;

          if (isApology || rsvp.status === 'cancelled') {
            return <span className="text-xs italic text-muted-foreground">Not applicable</span>;
          }

          if (tableInfo) {
            return (
              <div className="rounded-md bg-muted px-3 py-2 text-xs">
                <div className="font-semibold text-foreground">{tableInfo.tableName}</div>
                <div className="text-muted-foreground">Seat {tableInfo.seatNumber}</div>
              </div>
            );
          }
          
          return <span className="text-xs italic text-muted-foreground">Not assigned</span>;
        },
      },
      {
        id: 'mealChoices',
        header: event && isMultiTenantEvent(event) ? 'Tickets' : 'Meal Choices',
        enableSorting: false,
        cell: ({ row }) => {
          const rsvp = row.original;
          const isApology = !!rsvp.apologies;

          if (isApology || rsvp.status === 'cancelled') {
            return <span className="text-xs italic text-muted-foreground">Not applicable</span>;
          }

          const invitedByValue =
            typeof rsvp.invitedBy === 'string' && rsvp.invitedBy.trim().length > 0
              ? rsvp.invitedBy.trim()
              : undefined;
          const isInvitePendingResponse =
            !!invitedByValue && rsvp.status === 'pending';

          if (isInvitePendingResponse) {
            return (
              <div className="text-sm leading-relaxed">
                <Badge
                  variant="secondary"
                  className="inline-flex items-center gap-1 border-orange-200 bg-orange-100 px-2 py-0.5 text-xs text-orange-900 sm:px-3 sm:text-sm"
                >
                  <Clock className="h-3 w-3 shrink-0" aria-hidden />
                  Pending response
                </Badge>
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Invitation sent; this guest has not completed their RSVP yet.
                </p>
              </div>
            );
          }

          let mealDisplay: React.ReactNode = 'No meal selected';

          if (event && isMultiTenantEvent(event)) {
            const ticketEntries = (Array.isArray(rsvp.responses?.Tickets) ? rsvp.responses.Tickets : [] as any[])
              .filter((t: any) => t && t.qty > 0);
            mealDisplay = ticketEntries.length > 0 ? (
              <div className="space-y-0.5 text-xs">
                {ticketEntries.map((t: any, idx: number) => (
                  <div key={idx}>
                    <span className="font-bold">{t.name}</span>{' '}
                    <span className="text-muted-foreground">x{t.qty}</span>
                  </div>
                ))}
              </div>
            ) : (
              <span className="italic text-xs text-muted-foreground">No tickets recorded</span>
            );
          } else if (event?.tickets && event.tickets.length > 0 && Array.isArray(rsvp.responses?.Tickets)) {
            const ticketEntries = (rsvp.responses.Tickets as any[]).filter((t) => t && t.qty > 0);
            const normalizedTc = normalizeTicketCoursesToFormatA(
              (rsvp.responses as any)?.TicketCourses
            );
            if (ticketEntries.length > 0) {
              const ticketDisplays = ticketEntries.map((ticket, idx) => {
                const ticketType = event.tickets?.find(tt => tt.id === ticket.id)
                  ?? event.tickets?.find(tt => tt.name === ticket.name);
                const courseSelections: Array<{ courseName: string; mealChoice: string }> = [];
                if (ticketType?.courses) {
                  ticketType.courses.forEach((course) => {
                    const selection = getSelectionFromFormatA(
                      normalizedTc,
                      ticket.id,
                      course.id
                    );
                    if (selection) {
                      courseSelections.push({
                        courseName: course.name,
                        mealChoice: selection,
                      });
                    }
                  });
                }
                
                return (
                  <div key={`ticket-${idx}`} className={idx > 0 ? 'mt-3' : ''}>
                    <div className="font-bold text-sm mb-1">{ticket.name}</div>
                    {courseSelections.length > 0 ? (
                      <div className="space-y-0.5 text-xs text-muted-foreground">
                        {courseSelections.map((item, courseIdx) => {
                          const truncated = truncateText(item.mealChoice);
                          const needsTruncation = item.mealChoice.length > 25;
                          return (
                            <div key={courseIdx}>
                              <span className="font-bold">{item.courseName}:</span>{' '}
                              <span 
                                title={needsTruncation ? item.mealChoice : undefined}
                                className="cursor-help"
                              >
                                {truncated}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-xs text-muted-foreground italic">No meal choices</div>
                    )}
                  </div>
                );
              });
              
              mealDisplay = <div>{ticketDisplays}</div>;
            }
          } else if (event?.courses && event.courses.length > 0) {
            const courseSelections = event.courses
              .map((course) => {
                const selection = rsvp.responses?.[course.name];
                return selection ? { courseName: course.name, mealChoice: selection } : null;
              })
              .filter(Boolean) as Array<{ courseName: string; mealChoice: string }>;
            
            if (courseSelections.length > 0) {
              mealDisplay = (
                <div className="space-y-0.5 text-xs text-muted-foreground">
                  {courseSelections.map((item, idx) => {
                    const truncated = truncateText(item.mealChoice);
                    const needsTruncation = item.mealChoice.length > 25;
                    return (
                      <div key={idx}>
                        <span className="font-bold">{item.courseName}:</span>{' '}
                        <span 
                          title={needsTruncation ? item.mealChoice : undefined}
                          className="cursor-help"
                        >
                          {truncated}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            }
          } else if (rsvp.responses?.Meal) {
            const mealText = rsvp.responses.Meal;
            const truncated = truncateText(mealText);
            const needsTruncation = mealText.length > 25;
            mealDisplay = (
              <span 
                title={needsTruncation ? mealText : undefined}
                className="cursor-help text-xs text-muted-foreground"
              >
                {truncated}
              </span>
            );
          }

          return (
            <div className="text-sm text-muted-foreground leading-relaxed">
              {mealDisplay || <span className="italic text-xs">No meal choices recorded</span>}
            </div>
          );
        },
      },
      ...questionAnswerColumns,
      {
        id: 'amount',
        header: 'Amount',
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const totalFor = (r: RSVP) => getAttendanceExportAmounts(r, rsvps, event, invoiceMap)?.total ?? 0;
          return totalFor(rowA.original) - totalFor(rowB.original);
        },
        cell: ({ row }) => {
          const rsvp = row.original;
          if (rsvp.apologies || rsvp.status === 'cancelled') {
            return <span className="text-xs italic text-muted-foreground">Not applicable</span>;
          }
          const amounts = getAttendanceExportAmounts(rsvp, rsvps, event, invoiceMap);
          if (!amounts) {
            return null;
          }
          const { total, outstanding } = amounts;
          return (
            <div className="text-sm tabular-nums leading-tight">
              <div className="font-medium text-foreground">{formatCurrency(outstanding)}</div>
              <div className="text-xs text-muted-foreground">{formatCurrency(total)}</div>
            </div>
          );
        },
      },
      {
        id: 'payment',
        header: 'Payment',
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const paidA = getAttendanceRsvpPaymentState(rowA.original, rsvps, event, invoiceMap).displayPaid
            ? 1
            : 0;
          const paidB = getAttendanceRsvpPaymentState(rowB.original, rsvps, event, invoiceMap).displayPaid
            ? 1
            : 0;
          return paidA - paidB;
        },
        cell: ({ row }) => {
          const rsvp = row.original;
          const isApology = !!rsvp.apologies;

          if (isApology) {
            return (
              <Badge
                className="border-amber-200 bg-amber-100 px-2 py-0.5 text-xs text-amber-800 sm:px-3 sm:text-sm"
                variant="secondary"
              >
                Apologies
              </Badge>
            );
          }

          if (rsvp.status === 'cancelled') {
            return (
              <Badge variant="destructive" className="px-2 py-0.5 text-xs sm:px-3 sm:text-sm">
                Cancelled
              </Badge>
            );
          }

          const paymentState = getAttendanceRsvpPaymentState(rsvp, rsvps, event, invoiceMap);
          if (paymentState.hostPaysSeat) {
            return (
              <Badge variant="secondary" className="px-2 py-0.5 text-xs text-muted-foreground sm:px-3 sm:text-sm">
                Host Pays
              </Badge>
            );
          }
          if (paymentState.pendingVerification) {
            const payer = getInvoicePayerRsvp(rsvp, rsvps);
            const invId = payer.invoiceId?.trim();
            const inv = invId ? invoiceMap[invId] ?? null : null;
            const paymentReference = inv?.invoiceNumber || inv?.id || '';
            return (
              <div className="flex flex-col items-start gap-1">
                <Badge
                  variant="secondary"
                  className="bg-amber-100 px-2 py-0.5 text-xs text-amber-800 sm:px-3 sm:text-sm"
                >
                  Pending Verification
                </Badge>
                {paymentReference && (
                  <span className="text-xs text-muted-foreground">
                    Ref: <span className="font-mono font-medium">{paymentReference}</span>
                  </span>
                )}
              </div>
            );
          }

          return (
            <Badge
              variant={paymentState.displayPaid ? 'success' : 'warning'}
              className="px-2 py-0.5 text-xs sm:px-3 sm:text-sm"
            >
              {paymentState.displayPaid ? 'Paid' : 'Unpaid'}
            </Badge>
          );
        },
      },
      {
        id: 'actions',
        header: () => <div className="text-right">Actions</div>,
        enableSorting: false,
        cell: ({ row }) => {
          const rsvp = row.original;

          return (
            <div className="flex w-full flex-wrap items-center justify-center gap-2 md:justify-end md:gap-1.5">
              {isAdmin && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-10 min-w-10 shrink-0 px-0 sm:h-9 sm:min-w-0 sm:px-2 md:h-9"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyDashboardLink(rsvp);
                      }}
                      aria-label="Copy RSVP dashboard link"
                    >
                      <Link className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">Copy RSVP dashboard link</TooltipContent>
                </Tooltip>
              )}
              {!rsvp.apologies && rsvp.status !== 'cancelled' && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-10 min-w-10 shrink-0 px-0 sm:h-9 sm:min-w-0 sm:px-2 md:h-9"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleGeneratePlaceCardForAttendee(rsvp);
                        }}
                        disabled={generatingPlaceCardRsvpId === rsvp.id}
                        aria-label={
                          generatingPlaceCardRsvpId === rsvp.id
                            ? 'Generating place card'
                            : 'Download place card'
                        }
                      >
                        {generatingPlaceCardRsvpId === rsvp.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Contact className="h-4 w-4" />
                        )}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {generatingPlaceCardRsvpId === rsvp.id
                      ? 'Generating place card…'
                      : 'Download place card'}
                  </TooltipContent>
                </Tooltip>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-10 min-w-10 shrink-0 px-0 sm:h-9 sm:min-w-0 sm:px-2 md:h-9"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditRsvp(rsvp);
                    }}
                    aria-label="Edit RSVP"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">Edit RSVP</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-10 min-w-10 shrink-0 px-0 sm:h-9 sm:min-w-0 sm:px-2 md:h-9"
                    onClick={(e) => {
                      e.stopPropagation();
                      openPaymentsModal(rsvp);
                    }}
                    aria-label="View payments"
                  >
                    <Wallet className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">View payments</TooltipContent>
              </Tooltip>
              {canApprovePayments && !rsvp.apologies && rsvp.status !== 'cancelled' && (() => {
                const payer = getInvoicePayerRsvp(rsvp, rsvps);
                const invId = payer.invoiceId?.trim();
                const inv = invId ? invoiceMap[invId] ?? null : null;
                if (inv?.status !== 'pending_verification') return null;
                const isApproving = approvingPaymentRsvpId === rsvp.id;
                return (
                  <Tooltip key="approve-payment">
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-10 min-w-10 shrink-0 px-0 text-green-600 hover:text-green-700 sm:h-9 sm:min-w-0 sm:px-2 md:h-9"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleApprovePayment(rsvp);
                        }}
                        disabled={isApproving}
                        aria-label="Approve bank transfer payment"
                      >
                        {isApproving ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <CheckCircle className="h-4 w-4" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">Approve bank transfer</TooltipContent>
                  </Tooltip>
                );
              })()}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      'h-10 min-w-10 shrink-0 px-0 sm:h-9 sm:min-w-0 sm:px-2 md:h-9',
                      rsvp.apologies && 'text-amber-600',
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleApologies(rsvp);
                    }}
                    aria-label={rsvp.apologies ? 'Remove apology' : 'Mark as apologies'}
                  >
                    <Ban className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {rsvp.apologies ? 'Remove apology' : 'Mark as apologies'}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-10 min-w-10 shrink-0 px-0 sm:h-9 sm:min-w-0 sm:px-2 md:h-9"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteRsvp(rsvp);
                    }}
                    aria-label="Delete RSVP"
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">Delete RSVP</TooltipContent>
              </Tooltip>
            </div>
          );
        },
      },
    ];
  }, [
    rsvps,
    tableAssignments,
    expandedHosts,
    event,
    isAdmin,
    invoiceMap,
    openPaymentsModal,
    handleGeneratePlaceCardForAttendee,
    generatingPlaceCardRsvpId,
    canApprovePayments,
    approvingPaymentRsvpId,
    handleApprovePayment,
  ]);

  /** Show when this RSVP is linked to a host (any guest relationship, including guest-of-a-guest). */
  const showInviterPaysForSelect = useMemo(
    () => !!editForm.invitedBy?.trim(),
    [editForm.invitedBy],
  );

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading attendance list...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <TooltipProvider delayDuration={250}>
          <div className="flex flex-1 items-center justify-center">
            <Card className="max-w-md">
              <CardHeader>
                <CardTitle>Event Not Found</CardTitle>
                <CardDescription>The event you're looking for doesn't exist or you don't have access.</CardDescription>
              </CardHeader>
              <CardContent>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button onClick={() => navigate('/dashboard')} aria-label="Return to dashboard">
                      Return to Dashboard
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">Return to dashboard</TooltipContent>
                </Tooltip>
              </CardContent>
            </Card>
          </div>
        </TooltipProvider>
      </div>
    );
  }

  // Calculate total amount for RSVP including guests (supports tickets)
  const calculateTotalAmount = (rsvp: RSVP, guests: RSVP[]) => {
    if (!event) return 0;
    let baseTotal = 0;
    if (Array.isArray(rsvp.responses?.Tickets) && event.tickets && event.tickets.length > 0) {
      // Sum ticket lines for main RSVP only; guests handled below if they have their own tickets
      for (const line of rsvp.responses.Tickets as any[]) {
        const qty = Number(line?.qty || 0);
        const price = Number(line?.price || 0);
        baseTotal += qty * price;
      }
      // If a customPrice is set, it overrides computed base
      if (rsvp.customPrice !== undefined) baseTotal = rsvp.customPrice;
    } else {
      const defaultPrice = Number(event.costPerPerson || 0);
      baseTotal = rsvp.customPrice !== undefined ? rsvp.customPrice : defaultPrice;
    }
    const guestsTotal = guests.reduce((sum, guest) => {
      let guestAmount = 0;
      if (Array.isArray(guest.responses?.Tickets) && event.tickets && event.tickets.length > 0) {
        for (const line of guest.responses.Tickets as any[]) {
          const qty = Number(line?.qty || 0);
          const price = Number(line?.price || 0);
          guestAmount += qty * price;
        }
        if (guest.customPrice !== undefined) guestAmount = guest.customPrice;
      } else {
        const defaultPrice = Number(event.costPerPerson || 0);
        guestAmount = guest.customPrice !== undefined ? guest.customPrice : defaultPrice;
      }
      return sum + guestAmount;
    }, 0);
    return baseTotal + guestsTotal;
  };

  const apologyCount = rsvps.filter((rsvp) => rsvp.apologies).length;

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <TooltipProvider delayDuration={250}>
      <main className="flex-1 w-full min-w-0 overflow-x-hidden py-6 sm:py-8">
        <DashboardContentContainer className="sm:mx-0 sm:max-w-none">
          <div className="mb-4 sm:mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="-ml-2 h-auto px-2 sm:-ml-0 sm:px-4"
                  onClick={() => navigate(`/event/${eventId}`)}
                  aria-label="Back to event dashboard"
                >
                  <ArrowLeft className="mr-1.5 h-4 w-4 shrink-0 sm:mr-2" />
                  <span className="hidden sm:inline">Back to Event Dashboard</span>
                  <span className="sm:hidden">Back</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Back to event dashboard</TooltipContent>
            </Tooltip>
            <EventHeaderCredits unitId={unitId} />
          </div>

          <div className="mb-4 flex flex-col gap-4 sm:mb-6 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between sm:gap-3">
            <div className="min-w-0">
              <h1 className="text-2xl font-bold mb-1 sm:mb-2 sm:text-3xl">Attendance List</h1>
              <p className="break-words text-sm text-muted-foreground sm:text-base">
                {event.name} - {rsvps.length} total RSVPs
                {apologyCount > 0 && ` • ${apologyCount} apolog${apologyCount === 1 ? 'y' : 'ies'} recorded`}
              </p>
            </div>
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="default"
                    className="flex w-full items-center justify-center gap-2 sm:w-auto"
                    onClick={() => {
                      setGuestForm({
                        firstName: '',
                        lastName: '',
                        email: '',
                        rank: '',
                        additionalRank: '',
                        unit: '',
                        specialRequests: '',
                        invitedBy: '',
                        selectedTicketId: '',
                        ticketCourseSelections: {},
                        courseSelections: {},
                        notifyGuestByEmail: false,
                      });
                      setShowGuestModal(true);
                    }}
                    aria-label="Add guest"
                  >
                    <UserPlus className="h-4 w-4" />
                    Add Guest
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Add a new guest RSVP</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    className="flex w-full items-center justify-center gap-2 sm:w-auto"
                    onClick={() => {
                      setApologyForm({
                        firstName: '',
                        lastName: '',
                        email: '',
                        rank: '',
                        additionalRank: '',
                        unit: '',
                        specialRequests: '',
                      });
                      setShowApologyModal(true);
                    }}
                    aria-label="Record apology"
                  >
                    <Ban className="h-4 w-4" />
                    Record Apology
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Record an apology without a full RSVP</TooltipContent>
              </Tooltip>
              {rsvps.length > 0 && (
                <div className="relative w-full sm:w-auto" ref={exportMenuRef}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={() => setShowExportMenu(!showExportMenu)}
                        className="flex w-full items-center justify-center gap-2 sm:w-auto"
                        aria-label="Export attendance list"
                      >
                        <Download className="h-4 w-4" />
                        Export
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">Export PDF or CSV</TooltipContent>
                  </Tooltip>
                  {showExportMenu && (
                    <div className="absolute left-0 right-0 z-50 mt-2 max-h-[min(70vh,32rem)] w-full overflow-y-auto rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 sm:left-auto sm:right-0 sm:w-64 sm:max-h-none">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                          Export filter
                        </div>
                        <div className="mt-2 space-y-2">
                          <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="radio"
                              name="export-filter"
                              value="all"
                              checked={exportFilter === 'all'}
                              onChange={() => setExportFilter('all')}
                            />
                            All RSVPs
                          </label>
                          <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="radio"
                              name="export-filter"
                              value="attending"
                              checked={exportFilter === 'attending'}
                              onChange={() => setExportFilter('attending')}
                            />
                            Attending only
                          </label>
                          <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="radio"
                              name="export-filter"
                              value="apologies"
                              checked={exportFilter === 'apologies'}
                              onChange={() => setExportFilter('apologies')}
                            />
                            Apologies only
                          </label>
                        </div>
                      </div>
                      <div className="py-1" role="menu">
                        <button
                          onClick={handleExportPDF}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <FileText className="mr-3 h-4 w-4" />
                          Export as PDF
                        </button>
                        <button
                          onClick={handleExportCSV}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <FileSpreadsheet className="mr-3 h-4 w-4" />
                          Export as CSV
                        </button>
                      </div>
                      <div className="border-t border-gray-100 px-4 py-3">
                        <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                          Current filters
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          Status choice above plus any prefixes selected on the list.
                        </p>
                        <div className="mt-2 space-y-1" role="menu">
                          <button
                            type="button"
                            onClick={handleExportPDFWithCurrentFilters}
                            className="flex w-full items-center rounded px-2 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <FileText className="mr-3 h-4 w-4 shrink-0" />
                            Export as PDF (current filters)
                          </button>
                          <button
                            type="button"
                            onClick={handleExportCSVWithCurrentFilters}
                            className="flex w-full items-center rounded px-2 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <FileSpreadsheet className="mr-3 h-4 w-4 shrink-0" />
                            Export as CSV (current filters)
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <Card className="overflow-hidden shadow-md">
            <CardHeader className="space-y-1 p-4 pb-3 sm:p-6 sm:pb-4">
              <CardTitle className="text-xl sm:text-2xl">All RSVPs</CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                Complete list of attendees for this event
              </CardDescription>
            </CardHeader>
            <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
              {rsvps.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p>No RSVPs yet</p>
                </div>
              ) : (
                <DataTable
                  dense
                  equalColumnWidths
                  columns={columns}
                  data={rsvpsForTable}
                  searchPlaceholder="Search by name, email, unit, rank, ticket name..."
                  columnVisibilityDefaults={attendanceTableVisibilityDefaults}
                  columnIdsHiddenWhenNarrow={['email', 'unit', 'relationships', 'mealChoices', 'questionAnswers']}
                  stackActionColumnOnNarrow="actions"
                  toolbarAddon={
                    <div className="flex w-full min-w-0 flex-col gap-1.5 sm:max-w-md sm:shrink-0">
                      <Label>Prefixes</Label>
                      <MultiSelect
                        options={prefixFilterOptions}
                        value={prefixFilterSelection}
                        onChange={setPrefixFilterSelection}
                        placeholder="All prefixes"
                      />
                    </div>
                  }
                />
              )}
            </CardContent>
          </Card>
        </DashboardContentContainer>
      </main>

      {/* Edit RSVP Modal */}
      {editingRsvp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Edit RSVP</CardTitle>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" onClick={handleCancelEdit} aria-label="Close">
                      <X className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Close</TooltipContent>
                </Tooltip>
              </div>
              <CardDescription>Update RSVP information for {editingRsvp.name}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label htmlFor="edit-first-name">First name *</Label>
                    <Input
                      id="edit-first-name"
                      value={editForm.firstName}
                      onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-last-name">Last name *</Label>
                    <Input
                      id="edit-last-name"
                      value={editForm.lastName}
                      onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="edit-email">Email</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label htmlFor="edit-rank">Prefix</Label>
                    <select
                      id="edit-rank"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      value={editForm.rank || ''}
                      onChange={(e) => setEditForm({ ...editForm, rank: e.target.value })}
                    >
                      <option value="">Select prefix</option>
                      {editForm.rank &&
                        !unitTypePrefixes.some((prefix) => prefix.name === editForm.rank) && (
                          <option value={editForm.rank}>{editForm.rank}</option>
                        )}
                      {unitTypePrefixes.map((prefix) => (
                        <option key={prefix.id} value={prefix.name}>
                          {prefix.shortName
                            ? `${prefix.name} (${prefix.shortName})`
                            : prefix.name}
                        </option>
                      ))}
                    </select>
                    {!hasUnitTypePrefixes && (
                      <p className="mt-1 text-xs text-amber-600">
                        No prefixes are configured for this unit type.
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="edit-additional-rank">Rank</Label>
                    <Input
                      id="edit-additional-rank"
                      value={editForm.additionalRank || ''}
                      onChange={(e) => setEditForm({ ...editForm, additionalRank: e.target.value })}
                      placeholder="e.g., ProvJGW, PPADoC"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="edit-unit">Unit</Label>
                  <Input
                    id="edit-unit"
                    value={editForm.unit || ''}
                    onChange={(e) => setEditForm({ ...editForm, unit: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="edit-invited-by">Guest Of</Label>
                  <Select
                    value={editForm.invitedBy?.trim() ? editForm.invitedBy : NO_HOST_VALUE}
                    onValueChange={(value) => {
                      const nextInvitedBy = value === NO_HOST_VALUE ? '' : value;
                      setEditForm({
                        ...editForm,
                        invitedBy: nextInvitedBy,
                        inviterPaysFor:
                          nextInvitedBy.trim().length > 0 ? editForm.inviterPaysFor : true,
                      });
                    }}
                  >
                    <SelectTrigger id="edit-invited-by">
                      <SelectValue placeholder="Select primary RSVP (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_HOST_VALUE}>
                        No host (primary RSVP)
                      </SelectItem>
                      {rsvps
                        .filter((potentialHost) => potentialHost.id !== editingRsvp.id)
                        .sort(compareRsvpFirstNameLastName)
                        .map((potentialHost) => {
                          const hostValue =
                            (typeof potentialHost.userId === 'string' && potentialHost.userId.trim().length > 0
                              ? potentialHost.userId.trim()
                              : potentialHost.id) || potentialHost.id;
                          const label = [potentialHost.name, potentialHost.email]
                            .filter(Boolean)
                            .join(' • ');
                          return (
                            <SelectItem key={potentialHost.id} value={hostValue}>
                              {label || potentialHost.id}
                            </SelectItem>
                          );
                        })}
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Choose the RSVP this guest should be associated with. Leave blank if they are the main RSVP.
                  </p>
                </div>

                {showInviterPaysForSelect && (
                  <div>
                    <Label htmlFor="edit-inviter-pays-for">Who pays for this guest?</Label>
                    <Select
                      value={editForm.inviterPaysFor ? 'hosting' : 'guest'}
                      onValueChange={(v) =>
                        setEditForm({
                          ...editForm,
                          inviterPaysFor: v === 'hosting',
                        })
                      }
                    >
                      <SelectTrigger id="edit-inviter-pays-for">
                        <SelectValue placeholder="Select who pays" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hosting">Hosting member pays (inviter)</SelectItem>
                        <SelectItem value="guest">Guest pays</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Choose whether the person in Guest Of covers the cost, or this attendee pays for themselves.
                    </p>
                  </div>
                )}

                {event?.tickets && event.tickets.length > 0 ? (
                  isMultiTenantEvent(event) ? (
                    <div className="space-y-3">
                      <Label>Tickets</Label>
                      {event.tickets.map((ticket) => {
                        const currentTickets = Array.isArray(editForm.responses.Tickets) ? editForm.responses.Tickets as any[] : [];
                        const existingEntry = currentTickets.find((t) => t && t.id === ticket.id);
                        const qty = existingEntry?.qty ?? 0;

                        const setQty = (nextQty: number) => {
                          const clamped = Math.max(0, nextQty);
                          const withoutTicket = currentTickets.filter((t) => t && t.id !== ticket.id);
                          const nextTickets =
                            clamped > 0
                              ? [...withoutTicket, { id: ticket.id, name: ticket.name, price: ticket.price, qty: clamped }]
                              : withoutTicket;
                          setEditForm({
                            ...editForm,
                            responses: {
                              ...editForm.responses,
                              Tickets: nextTickets,
                            },
                          });
                        };

                        return (
                          <div key={ticket.id} className="flex flex-wrap items-center justify-between gap-3 rounded border p-3">
                            <div className="min-w-0">
                              <p className="font-medium">{ticket.name}</p>
                              <p className="text-sm text-muted-foreground">{formatCurrency(ticket.price || 0)} each</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => setQty(qty - 1)} disabled={qty <= 0}>
                                <Minus className="h-4 w-4" />
                              </Button>
                              <Input
                                type="number"
                                min={0}
                                value={qty}
                                onChange={(e) => setQty(parseInt(e.target.value, 10) || 0)}
                                className="w-16 shrink-0 text-center"
                              />
                              <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => setQty(qty + 1)}>
                                <Plus className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label>Tickets</Label>
                    </div>
                    {(() => {
                      const currentTickets = Array.isArray(editForm.responses.Tickets) ? editForm.responses.Tickets as any[] : [];
                      // If no tickets selected, show at least one empty entry
                      const ticketEntries = currentTickets.length > 0 ? currentTickets : [{ id: '', name: '', price: 0, qty: 1 }];

                      return ticketEntries.map((ticketEntry, entryIndex) => {
                        const selectedTicketType = event.tickets?.find(t => t.id === ticketEntry.id);
                        // Always use quantity 1 for single guest selection
                        const qty = 1;
                        
                        return (
                          <div key={`entry-${entryIndex}`} className="rounded border p-4 space-y-4">
                            <div className="grid gap-3 md:grid-cols-2 items-end">
                              <div>
                                <Label>Ticket Type *</Label>
                                <Select
                                  value={ticketEntry.id || ''}
                                  onValueChange={(ticketId) => {
                                    const ticket = event.tickets?.find(t => t.id === ticketId);
                                    if (!ticket) return;
                                    
                                    const updated = [...currentTickets];
                                    // Always set quantity to 1 when selecting a ticket type
                                    // Use ticket's default price when selecting, but allow editing
                                    if (updated[entryIndex]) {
                                      updated[entryIndex] = { id: ticket.id, name: ticket.name, price: ticket.price, qty: 1 };
                                    } else {
                                      updated.push({ id: ticket.id, name: ticket.name, price: ticket.price, qty: 1 });
                                    }
                                    
                                    setEditForm({
                                      ...editForm,
                                      responses: {
                                        ...editForm.responses,
                                        Tickets: updated,
                                      },
                                    });
                                  }}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select ticket type" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {event.tickets?.map((t) => (
                                      <SelectItem key={t.id} value={t.id}>
                                        {t.name} - {formatCurrency(t.price || 0)}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label>Price (£)</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={ticketEntry.price || ''}
                                  onChange={(e) => {
                                    const newPrice = parseFloat(e.target.value) || 0;
                                    const updated = [...currentTickets];
                                    if (updated[entryIndex]) {
                                      updated[entryIndex] = { ...updated[entryIndex], price: newPrice };
                                    }
                                    setEditForm({
                                      ...editForm,
                                      responses: {
                                        ...editForm.responses,
                                        Tickets: updated,
                                      },
                                    });
                                  }}
                                  placeholder="0.00"
                                />
                              </div>
                            </div>
                            
                            {/* Per-ticket course selections - show immediately when ticket type is selected */}
                            {selectedTicketType && selectedTicketType.courses && selectedTicketType.courses.length > 0 && (
                              <div className="mt-3">
                                <div className="space-y-3">
                                  <div className="rounded-md border p-3">
                                    <div className="font-medium mb-3">{selectedTicketType.name}</div>
                                    {(selectedTicketType.courses || []).map((c) => {
                                      const tc = (editForm.responses as any).TicketCourses || {};
                                      const ticketIndex = 1; // Always 1 for single guest
                                      return (
                                        <div key={c.id} className="space-y-2 mb-3">
                                          <Label className="text-sm font-normal">{c.name} *</Label>
                                          <select
                                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                            value={tc?.[selectedTicketType.id]?.[ticketIndex]?.[c.id] || ''}
                                            onChange={(e) => {
                                              const value = e.target.value;
                                              setEditForm((prev) => {
                                                const prevTc = (prev.responses as any).TicketCourses || {};
                                                const ticketMap = prevTc[selectedTicketType.id] || {};
                                                const instMap = ticketMap[ticketIndex] || {};
                                                const updatedTc = {
                                                  ...prevTc,
                                                  [selectedTicketType.id]: {
                                                    ...ticketMap,
                                                    [ticketIndex]: { ...instMap, [c.id]: value },
                                                  },
                                                };
                                                return {
                                                  ...prev,
                                                  responses: { ...prev.responses, TicketCourses: updatedTc },
                                                };
                                              });
                                            }}
                                          >
                                            <option value="">Select an option</option>
                                            {getCourseSelectOptions(c.options).map((option) => (
                                              <option key={option} value={option}>
                                                {option}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                  )
                ) : (
                  event?.courses && event.courses.length > 0 && (
                    <div className="space-y-3">
                      <Label>Meal Choices</Label>
                      {event.courses.map((course) => (
                        <div key={course.id}>
                          <Label htmlFor={`edit-course-${course.id}`} className="text-sm font-normal">
                            {course.name}
                          </Label>
                          <select
                            id={`edit-course-${course.id}`}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            value={editForm.responses[course.name] || ''}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                responses: {
                                  ...editForm.responses,
                                  [course.name]: e.target.value,
                                },
                              })
                            }
                          >
                            <option value="">Select an option</option>
                            {getCourseSelectOptions(course.options).map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  )
                )}

                <div>
                  <Label htmlFor="edit-special-requests">Special Requests / Dietary Requirements</Label>
                  <Textarea
                    id="edit-special-requests"
                    value={editForm.specialRequests || ''}
                    onChange={(e) => setEditForm({ ...editForm, specialRequests: e.target.value })}
                    rows={3}
                  />
                </div>

                <div className="space-y-4">
                  <div className="space-y-1">
                    <label htmlFor="edit-notify-guest" className="flex items-center gap-2 text-sm font-medium">
                      <input
                        id="edit-notify-guest"
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                        checked={editForm.notifyGuestByEmail}
                        onChange={(e) => setEditForm({ ...editForm, notifyGuestByEmail: e.target.checked })}
                      />
                      Email guest about this update
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Sends an email with their updated RSVP details. Leave unchecked to save without notifying them.
                    </p>
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="edit-apologies" className="flex items-center gap-2 text-sm font-medium">
                      <input
                        id="edit-apologies"
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                        checked={editForm.apologies}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setEditForm((prev) => ({
                            ...prev,
                            apologies: checked,
                            status: checked
                              ? (prev.status === 'cancelled' ? prev.status : 'declined')
                              : (prev.status === 'declined' ? 'confirmed' : prev.status),
                          }));
                        }}
                      />
                      Mark as apologies
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Apologies remain visible on the attendance list but do not count toward totals.
                    </p>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="edit-status">Status</Label>
                    <select
                      id="edit-status"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={editForm.status}
                      onChange={(e) => {
                        const value = e.target.value as RSVPStatus;
                        setEditForm((prev) => ({
                          ...prev,
                          status: value,
                          apologies:
                            value === 'declined'
                              ? true
                              : value === 'confirmed' || value === 'pending' || value === 'waitlist'
                                ? false
                                : prev.apologies,
                        }));
                      }}
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="declined">Declined</option>
                      <option value="waitlist">Waitlist</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                    <p className="text-xs text-muted-foreground">
                      Sets the RSVP status directly. Declined and cancelled RSVPs do not count toward totals.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveEdit}>
                    Save Changes
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {showApologyModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-xl">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Record Apology</CardTitle>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => !creatingApology && setShowApologyModal(false)}
                        disabled={creatingApology}
                        aria-label="Close"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Close</TooltipContent>
                </Tooltip>
              </div>
              <CardDescription>
                Add a guest to the attendance list with their apologies recorded.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label htmlFor="apology-first-name">First Name *</Label>
                    <Input
                      id="apology-first-name"
                      value={apologyForm.firstName}
                      onChange={(e) => setApologyForm({ ...apologyForm, firstName: e.target.value })}
                      disabled={creatingApology}
                    />
                  </div>
                  <div>
                    <Label htmlFor="apology-last-name">Last Name *</Label>
                    <Input
                      id="apology-last-name"
                      value={apologyForm.lastName}
                      onChange={(e) => setApologyForm({ ...apologyForm, lastName: e.target.value })}
                      disabled={creatingApology}
                    />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label htmlFor="apology-email">Email *</Label>
                    <Input
                      id="apology-email"
                      type="email"
                      value={apologyForm.email}
                      onChange={(e) => setApologyForm({ ...apologyForm, email: e.target.value })}
                      disabled={creatingApology}
                    />
                  </div>
                  <div>
                    <Label htmlFor="apology-unit">Unit</Label>
                    <Input
                      id="apology-unit"
                      value={apologyForm.unit}
                      onChange={(e) => setApologyForm({ ...apologyForm, unit: e.target.value })}
                      disabled={creatingApology}
                    />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label htmlFor="apology-rank">Prefix</Label>
                    <Input
                      id="apology-rank"
                      value={apologyForm.rank}
                      onChange={(e) => setApologyForm({ ...apologyForm, rank: e.target.value })}
                      disabled={creatingApology}
                    />
                  </div>
                  <div>
                    <Label htmlFor="apology-additional-rank">Rank</Label>
                    <Input
                      id="apology-additional-rank"
                      value={apologyForm.additionalRank}
                      onChange={(e) => setApologyForm({ ...apologyForm, additionalRank: e.target.value })}
                      disabled={creatingApology}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="apology-special-requests">Notes</Label>
                  <Textarea
                    id="apology-special-requests"
                    rows={3}
                    value={apologyForm.specialRequests}
                    onChange={(e) => setApologyForm({ ...apologyForm, specialRequests: e.target.value })}
                    disabled={creatingApology}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Optional notes about the apology or special considerations.
                  </p>
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowApologyModal(false)}
                    disabled={creatingApology}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleCreateApology} disabled={creatingApology}>
                    {creatingApology ? 'Recording...' : 'Record Apology'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {showGuestModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Add Guest</CardTitle>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => !creatingGuest && setShowGuestModal(false)}
                        disabled={creatingGuest}
                        aria-label="Close"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Close</TooltipContent>
                </Tooltip>
              </div>
              <CardDescription>
                Add a guest to the attendance list with their ticket choice and meal options.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label htmlFor="guest-first-name">First Name *</Label>
                    <Input
                      id="guest-first-name"
                      value={guestForm.firstName}
                      onChange={(e) => setGuestForm({ ...guestForm, firstName: e.target.value })}
                      disabled={creatingGuest}
                    />
                  </div>
                  <div>
                    <Label htmlFor="guest-last-name">Last Name *</Label>
                    <Input
                      id="guest-last-name"
                      value={guestForm.lastName}
                      onChange={(e) => setGuestForm({ ...guestForm, lastName: e.target.value })}
                      disabled={creatingGuest}
                    />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label htmlFor="guest-email">Email *</Label>
                    <Input
                      id="guest-email"
                      type="email"
                      value={guestForm.email}
                      onChange={(e) => setGuestForm({ ...guestForm, email: e.target.value })}
                      disabled={creatingGuest}
                    />
                  </div>
                  <div>
                    <Label htmlFor="guest-unit">Unit</Label>
                    <Input
                      id="guest-unit"
                      value={guestForm.unit}
                      onChange={(e) => setGuestForm({ ...guestForm, unit: e.target.value })}
                      disabled={creatingGuest}
                    />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label htmlFor="guest-rank">Prefix</Label>
                    <select
                      id="guest-rank"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      value={guestForm.rank || ''}
                      onChange={(e) => setGuestForm({ ...guestForm, rank: e.target.value })}
                      disabled={creatingGuest}
                    >
                      <option value="">Select prefix</option>
                      {guestForm.rank &&
                        !unitTypePrefixes.some((prefix) => prefix.name === guestForm.rank) && (
                          <option value={guestForm.rank}>{guestForm.rank}</option>
                        )}
                      {unitTypePrefixes.map((prefix) => (
                        <option key={prefix.id} value={prefix.name}>
                          {prefix.shortName
                            ? `${prefix.name} (${prefix.shortName})`
                            : prefix.name}
                        </option>
                      ))}
                    </select>
                    {!hasUnitTypePrefixes && (
                      <p className="mt-1 text-xs text-amber-600">
                        No prefixes are configured for this unit type.
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="guest-additional-rank">Rank</Label>
                    <Input
                      id="guest-additional-rank"
                      value={guestForm.additionalRank}
                      onChange={(e) => setGuestForm({ ...guestForm, additionalRank: e.target.value })}
                      disabled={creatingGuest}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="guest-invited-by">Guest Of</Label>
                  <Select
                    value={guestForm.invitedBy?.trim() ? guestForm.invitedBy : NO_HOST_VALUE}
                    onValueChange={(value) => {
                      const nextInvitedBy = value === NO_HOST_VALUE ? '' : value;
                      setGuestForm({
                        ...guestForm,
                        invitedBy: nextInvitedBy,
                      });
                    }}
                    disabled={creatingGuest}
                  >
                    <SelectTrigger id="guest-invited-by">
                      <SelectValue placeholder="Select primary RSVP (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_HOST_VALUE}>
                        No host (primary RSVP)
                      </SelectItem>
                      {rsvps.map((potentialHost) => {
                        const hostValue =
                          (typeof potentialHost.userId === 'string' && potentialHost.userId.trim().length > 0
                            ? potentialHost.userId.trim()
                            : potentialHost.id) || potentialHost.id;
                        const label = [potentialHost.name, potentialHost.email]
                          .filter(Boolean)
                          .join(' • ');
                        return (
                          <SelectItem key={potentialHost.id} value={hostValue}>
                            {label || potentialHost.id}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Choose the RSVP this guest should be associated with. Leave blank if they are the main RSVP.
                  </p>
                </div>

                {event?.tickets && event.tickets.length > 0 ? (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="guest-ticket">Ticket Type *</Label>
                      <Select
                        value={guestForm.selectedTicketId}
                        onValueChange={(ticketId) => {
                          const ticket = event.tickets?.find(t => t.id === ticketId);
                          if (!ticket) return;
                          
                          setGuestForm({
                            ...guestForm,
                            selectedTicketId: ticketId,
                            // Initialize course selections for this ticket
                            ticketCourseSelections: {
                              ...guestForm.ticketCourseSelections,
                              [ticketId]: {
                                1: {},
                              },
                            },
                          });
                        }}
                        disabled={creatingGuest}
                      >
                        <SelectTrigger id="guest-ticket">
                          <SelectValue placeholder="Select ticket type" />
                        </SelectTrigger>
                        <SelectContent>
                          {event.tickets.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.name} - {formatCurrency(t.price || 0)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {guestForm.selectedTicketId && (() => {
                      const selectedTicket = event.tickets?.find(t => t.id === guestForm.selectedTicketId);
                      if (!selectedTicket?.courses || selectedTicket.courses.length === 0) {
                        return null;
                      }

                      const instanceIndex = 1;
                      const currentSelections = guestForm.ticketCourseSelections[guestForm.selectedTicketId]?.[instanceIndex] || {};

                      return (
                        <div className="rounded-md border p-4 space-y-3">
                          <div className="font-medium mb-3">Meal Choices for {selectedTicket.name}</div>
                          {selectedTicket.courses.map((course) => (
                            <div key={course.id} className="space-y-2">
                              <Label className="text-sm font-normal">{course.name} *</Label>
                              <select
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                value={currentSelections[course.id] || ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setGuestForm((prev) => {
                                    const prevTc = prev.ticketCourseSelections || {};
                                    const ticketMap = prevTc[guestForm.selectedTicketId] || {};
                                    const instMap = ticketMap[instanceIndex] || {};
                                    const updatedTc = {
                                      ...prevTc,
                                      [guestForm.selectedTicketId]: {
                                        ...ticketMap,
                                        [instanceIndex]: { ...instMap, [course.id]: value },
                                      },
                                    };
                                    return {
                                      ...prev,
                                      ticketCourseSelections: updatedTc,
                                    };
                                  });
                                }}
                                disabled={creatingGuest}
                              >
                                <option value="">Select an option</option>
                                {getCourseSelectOptions(course.options).map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                ) : event?.courses && event.courses.length > 0 ? (
                  <div className="space-y-3">
                    <Label>Meal Choices</Label>
                    {event.courses.map((course) => (
                      <div key={course.id}>
                        <Label htmlFor={`guest-course-${course.id}`} className="text-sm font-normal">
                          {course.name}
                        </Label>
                        <select
                          id={`guest-course-${course.id}`}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          value={guestForm.courseSelections[course.name] || ''}
                          onChange={(e) =>
                            setGuestForm({
                              ...guestForm,
                              courseSelections: {
                                ...guestForm.courseSelections,
                                [course.name]: e.target.value,
                              },
                            })
                          }
                          disabled={creatingGuest}
                        >
                          <option value="">Select an option</option>
                          {getCourseSelectOptions(course.options).map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                ) : null}

                <div>
                  <Label htmlFor="guest-special-requests">Special Requests / Dietary Requirements</Label>
                  <Textarea
                    id="guest-special-requests"
                    rows={3}
                    value={guestForm.specialRequests}
                    onChange={(e) => setGuestForm({ ...guestForm, specialRequests: e.target.value })}
                    disabled={creatingGuest}
                  />
                </div>

                <div className="space-y-1">
                  <label htmlFor="guest-notify-email" className="flex items-center gap-2 text-sm font-medium">
                    <input
                      id="guest-notify-email"
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      checked={guestForm.notifyGuestByEmail}
                      onChange={(e) => setGuestForm({ ...guestForm, notifyGuestByEmail: e.target.checked })}
                      disabled={creatingGuest}
                    />
                    Email guest about their RSVP
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Sends an email with their RSVP details. Leave unchecked to add the guest without notifying them.
                  </p>
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowGuestModal(false)}
                    disabled={creatingGuest}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleCreateGuest} disabled={creatingGuest}>
                    {creatingGuest ? 'Adding...' : 'Add Guest'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <InvoicePaymentsModal
        open={paymentModalOpen}
        onOpenChange={setPaymentModalOpen}
        event={event}
        invoice={paymentModalInvoice}
        rsvps={rsvps}
        onInvoiceUpdated={handleInvoiceUpdatedFromModal}
      />
      </TooltipProvider>
    </div>
  );
}

